import React, { useState, useEffect, useRef } from 'react';
import { Heart, Menu, X, ChevronDown, ChevronLeft, ChevronRight, Lock, Download, Trash2, Users, Crown, MessageSquare, Send, Hotel, MapPin, Phone, Globe, Camera } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { QRCodeCanvas } from 'qrcode.react';
import logo from './logo.svg';

// CRA (react-scripts) / Jest kann Package-Exports-Subpaths teils nicht auflösen.
// Für Tests ist Speed Insights nicht relevant → im Test-Env als Noop rendern.
const SpeedInsights =
  process.env.NODE_ENV === 'test'
    ? () => null
    : (() => {
        try {
          // eslint-disable-next-line global-require
          return require('@vercel/speed-insights/react').SpeedInsights;
        } catch {
          return () => null;
        }
      })();

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const firebaseReady = [
  firebaseConfig.apiKey,
  firebaseConfig.authDomain,
  firebaseConfig.projectId,
  firebaseConfig.storageBucket,
  firebaseConfig.messagingSenderId,
  firebaseConfig.appId
].every((value) => typeof value === 'string' && value.trim() !== '');

const app = firebaseReady ? initializeApp(firebaseConfig) : null;
const auth = app ? getAuth(app) : null;
const db = app ? getFirestore(app) : null;

const WEDDING_DATE = new Date(2026, 7, 11, 10, 30, 0);

// Kosten pro Person (Gäste: 0–2 J. 0€, 3–10 J. 80€, ab 11 J. 185€; Dienstleister 120€)
const PRICE_BABY = 0;      // 0–2 Jahre
const PRICE_CHILD = 80;    // 3–10 Jahre
const PRICE_ADULT = 185;   // ab 11 Jahre
const PRICE_VENDOR = 120;  // pro Dienstleister

// Übersetzungen
const translations = {
  de: {
    home: 'Home',
    ourStory: 'Unsere Geschichte',
    details: 'Details',
    rsvp: 'RSVP',
    location: 'Anfahrt',
    areGettingMarried: 'Heiraten',
    untilBigDay: 'Bis zum großen Tag',
    days: 'Tage',
    hours: 'Stunden',
    minutes: 'Minuten',
    trani: 'Trani',
    whereOurLove: 'Wo unsere Liebe offiziell besiegelt wird',
    loveStoryTitle: '💕 Unsere Liebesgeschichte',
    weddingDetails: 'Hochzeitsdetails',
    ceremony: 'Trauung',
    reception: 'Feier',
    dressCode: 'Dresscode',
    dressCodeText: 'Bitte kleiden Sie sich festlich. Bitte vermeiden Sie Weiß.',
    dressCodeColorPalette: 'Farbpalette: Hellblau, Salbeigrün, Hellgrün, zartes Gelb und warmes Beige.',
    respondBy: 'Bitte antwortet uns bis zum 01. Mai 2026',
    firstName: 'Vorname',
    lastName: 'Nachname',
    email: 'E-Mail',
    willYouAttend: 'Werden Sie an unserer Hochzeit teilnehmen?',
    yesAttending: 'Ja, ich komme!',
    noAttending: 'Leider nicht',
    guests: 'Gäste',
    addGuest: '+ Weiteren Gast hinzufügen',
    ageNote: '⚠️ Alter bitte ganz wichtig nur bei Kindern ausfüllen!',
    guestName: 'Name',
    guestAge: 'Alter',
    stayQuestion: 'Habt ihr bereits eine Unterkunft gebucht?',
    stayYes: 'Ja, wir haben schon eine Unterkunft',
    stayNo: 'Nein, wir brauchen noch eine Empfehlung',
    dietary: 'Was sind Ihre oder Ihrer Familienmitglieder diätetische Einschränkungen?',
    pleaseSelect: 'Bitte auswählen',
    vegetarian: 'Vegetarisch',
    vegan: 'Vegan',
    fish: 'Fisch',
    chicken: 'Hähnchen',
    allergies: 'Hast du oder eins deiner Familienmitglieder irgendwelche Lebensmittel Allergien?',
    message: 'Gibt es noch etwas, das Sie uns sagen möchten?',
    messagePlaceholder: 'Ich hoffe, wir sehen uns bald...',
    submit: 'Antwort senden',
    thankYou: 'Vielen Dank!',
    receivedResponse: 'Wir haben deine Antwort erhalten 💕',
    gettingThere: 'Anfahrt',
    byCar: '🚗 Mit dem Auto',
    parkingInfo: 'Parkplätze sind kostenpflichtig und können über EasyPark reserviert werden.',
    tip: '💡 Tipp',
    tipText: 'Die Altstadt - bitte frühzeitig anreisen!',
    adminLogin: 'Admin Login',
    password: 'Passwort',
    login: 'Einloggen',
    logout: 'Abmelden',
    wrongPassword: 'Falsches Passwort!',
    adminPanel: 'Admin Panel',
    guestList: 'Gästeliste',
    totalGuests: 'Gesamt',
    confirmed: 'Zusagen',
    declined: 'Absagen',
    exportCSV: 'Als CSV exportieren',
    exportExcel: 'Als Excel exportieren',
    delete: 'Löschen',
    timelineIntro: 'Joanna und Domenico feiern ihre kirchliche Hochzeit am 11. August 2026 in Trani – hier findet ihr den Ablauf des Tages.',
    timelinePunctualNotice: 'Bitte kommt alle um 10:30 Uhr da, da die Zeremonie um 10:30 Uhr pünktlich anfängt und nach der Braut kommt keiner mehr in die Kirche rein!',
    timelineCeremonyTitle: 'Kirchliche Trauung',
    timelineCeremonyDesc: 'Die Trauung findet in der Kathedrale von Trani um 10:30 Uhr statt.',
    timelineArrivalTitle: 'Ankunft in der Tenuta',
    timelineArrivalDesc: 'Willkommens-Aperitif für alle Gäste.',
    timelineLunchTitle: 'Traditionelles apulisches Mittagessen',
    timelineLunchDesc: 'Gänge wechseln sich mit Tanz- und Unterhaltungsmomenten ab.',
    timelineDessertTitle: 'Dessertbuffet',
    timelineDessertDesc: 'Apulische, neapolitanische und sizilianische Spezialitäten.',
    timelineCakeTitle: 'Hochzeitstorte',
    timelineCakeDesc: 'Auszug der Hochzeitstorte - ein emotionaler Moment mit den Gästen.',
    timelinePartyTitle: 'After-Party im Lavinia Club',
    timelinePartyDesc: 'Feier mit Premium-Drinks und Musik in einzigartiger Atmosphäre.',
    submitRSVP: 'RSVP Absenden',
    rsvpThankYou: 'Vielen Dank!',
    rsvpThankYouMessage: 'Deine RSVP wurde erfolgreich übermittelt. Wir freuen uns auf dich!',
    faqTitle: 'Häufige Fragen',
    faqParkingQ: 'Gibt es Parkmöglichkeiten?',
    faqParkingA: 'Ja, in Trani gibt es kostenpflichtige Parkplätze, die z.B. über EasyPark gebucht werden können. Wir empfehlen eine frühe Anreise wegen der Altstadt.',
    faqKidsQ: 'Sind Kinder eingeladen?',
    faqKidsA: 'Ja, Kinder sind herzlich willkommen. Bitte tragt sie in der RSVP mit Namen und Alter ein, damit wir besser planen können.',
    faqGiftsQ: 'Was wünscht ihr euch zur Hochzeit?',
    faqGiftsA: 'Euer Kommen ist unser größtes Geschenk. Wer trotzdem etwas geben möchte, darf uns gerne mit einem Beitrag zu unserer Italien-Reise unterstützen.',
    guestbook: 'Gästebuch',
    guestbookSubtitle: 'Hinterlasst uns eure Glückwünsche',
    guestbookName: 'Dein Name',
    guestbookMessage: 'Deine Nachricht',
    guestbookSubmit: 'Nachricht senden',
    guestbookThankYou: 'Vielen Dank für deine lieben Worte!',
    photos: 'Galerie',
    photosSubtitle: 'Teilt eure schönsten Momente der Hochzeit mit uns',
    photosName: 'Dein Name',
    photosSelect: 'Fotos auswählen & hochladen',
    photosUploading: 'Wird hochgeladen…',
    photosThankYou: 'Danke für eure Fotos!',
    photosEmpty: 'Noch keine Fotos – ladet die ersten hoch!',
    photosNameRequired: 'Bitte zuerst deinen Namen eingeben.',
    photosUploadError: 'Upload fehlgeschlagen. Bitte erneut versuchen.',
    photosHint: 'Eure Fotos erscheinen direkt hier in der Galerie.',
    hotels: 'Hotel-Empfehlungen',
    hotelsSubtitle: 'Unsere Empfehlungen für euren Aufenthalt in Trani'
  },
  en: {
    home: 'Home',
    ourStory: 'Our Story',
    details: 'Details',
    rsvp: 'RSVP',
    location: 'Location',
    areGettingMarried: 'Are Getting Married',
    untilBigDay: 'Until The Big Day',
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    trani: 'Trani',
    whereOurLove: 'Where our love will be officially sealed',
    loveStoryTitle: '💕 Our Love Story',
    weddingDetails: 'Wedding Details',
    ceremony: 'Ceremony',
    reception: 'Reception',
    dressCode: 'Dress Code',
    dressCodeText: 'Please dress festively. Please avoid white.',
    dressCodeColorPalette: 'Color palette: light blue, sage green, light green, soft yellow and warm beige.',
    respondBy: 'Please respond by May 01, 2026',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    willYouAttend: 'Will you be attending our wedding?',
    yesAttending: 'Yes, I\'m coming!',
    noAttending: 'Unfortunately not',
    guests: 'Guests',
    addGuest: '+ Add Another Guest',
    ageNote: '⚠️ Age only for children please!',
    guestName: 'Name',
    guestAge: 'Age',
    stayQuestion: 'Do you already have accommodation?',
    stayYes: 'Yes, we already have a place to stay',
    stayNo: 'No, we still need a recommendation',
    dietary: 'What are your or your family members\' dietary restrictions?',
    pleaseSelect: 'Please select',
    vegetarian: 'Vegetarian',
    vegan: 'Vegan',
    fish: 'Fish',
    chicken: 'Chicken',
    allergies: 'Do you or any family members have food allergies?',
    message: 'Is there anything else you\'d like to tell us?',
    messagePlaceholder: 'Hope to see you soon...',
    submit: 'Submit Response',
    thankYou: 'Thank You!',
    receivedResponse: 'We received your response 💕',
    gettingThere: 'Getting There',
    byCar: '🚗 By Car',
    parkingInfo: 'Parking is paid and can be reserved via EasyPark.',
    tip: '💡 Tip',
    tipText: 'The old town - please arrive early!',
    adminLogin: 'Admin Login',
    password: 'Password',
    login: 'Login',
    logout: 'Logout',
    wrongPassword: 'Wrong password!',
    adminPanel: 'Admin Panel',
    guestList: 'Guest List',
    totalGuests: 'Total',
    confirmed: 'Confirmed',
    declined: 'Declined',
    exportCSV: 'Export as CSV',
    exportExcel: 'Export as Excel',
    delete: 'Delete',
    timelineIntro: 'Joanna and Domenico celebrate their church wedding on August 11, 2026 in Trani – here is the plan for the day.',
    timelinePunctualNotice: 'Please arrive by 10:30 AM. The ceremony starts promptly at 10:30 AM, and after the bride arrives, no one will be allowed to enter the church.',
    timelineCeremonyTitle: 'Church Ceremony',
    timelineCeremonyDesc: 'The ceremony takes place at Trani Cathedral at 10:30 AM.',
    timelineArrivalTitle: 'Arrival at the Tenuta',
    timelineArrivalDesc: 'Welcome aperitif for all guests.',
    timelineLunchTitle: 'Traditional Apulian Lunch',
    timelineLunchDesc: 'Courses alternate with dance and entertainment moments.',
    timelineDessertTitle: 'Dessert Buffet',
    timelineDessertDesc: 'Apulian, Neapolitan and Sicilian specialties.',
    timelineCakeTitle: 'Wedding Cake',
    timelineCakeDesc: 'Wedding cake cutting - an emotional moment with the guests.',
    timelinePartyTitle: 'After-Party at Lavinia Club',
    timelinePartyDesc: 'Celebration with premium drinks and music in a unique atmosphere.',
    submitRSVP: 'Submit RSVP',
    rsvpThankYou: 'Thank You!',
    rsvpThankYouMessage: 'Your RSVP has been successfully submitted. We look forward to seeing you!',
    faqTitle: 'Frequently Asked Questions',
    faqParkingQ: 'Is there parking available?',
    faqParkingA: 'Yes, there are paid parking options in Trani which can be booked via EasyPark. We recommend arriving early because of the old town.',
    faqKidsQ: 'Are children invited?',
    faqKidsA: 'Yes, children are very welcome. Please add them with name and age in the RSVP so we can plan accordingly.',
    faqGiftsQ: 'What would you like as a gift?',
    faqGiftsA: 'Your presence is our greatest gift. If you still wish to give something, a contribution to our Italy trip would make us very happy.',
    guestbook: 'Guestbook',
    guestbookSubtitle: 'Leave us your best wishes',
    guestbookName: 'Your Name',
    guestbookMessage: 'Your Message',
    guestbookSubmit: 'Send Message',
    guestbookThankYou: 'Thank you for your kind words!',
    photos: 'Gallery',
    photosSubtitle: 'Share your favorite moments of the wedding with us',
    photosName: 'Your Name',
    photosSelect: 'Select & upload photos',
    photosUploading: 'Uploading…',
    photosThankYou: 'Thank you for your photos!',
    photosEmpty: 'No photos yet – be the first to upload!',
    photosNameRequired: 'Please enter your name first.',
    photosUploadError: 'Upload failed. Please try again.',
    photosHint: 'Your photos will appear right here in the gallery.',
    hotels: 'Hotel Recommendations',
    hotelsSubtitle: 'Our recommendations for your stay in Trani'
  },
  it: {
    home: 'Home',
    ourStory: 'La Nostra Storia',
    details: 'Dettagli',
    rsvp: 'RSVP',
    location: 'Posizione',
    areGettingMarried: 'Si Sposano',
    untilBigDay: 'Fino al Grande Giorno',
    days: 'Giorni',
    hours: 'Ore',
    minutes: 'Minuti',
    trani: 'Trani',
    whereOurLove: 'Dove il nostro amore sarà ufficialmente sigillato',
    loveStoryTitle: '💕 La Nostra Storia d\'Amore',
    weddingDetails: 'Dettagli del Matrimonio',
    ceremony: 'Cerimonia',
    reception: 'Ricevimento',
    dressCode: 'Codice di Abbigliamento',
    dressCodeText: 'Si prega di vestirsi in modo festoso. Si prega di evitare il bianco.',
    dressCodeColorPalette: 'Palette colori: azzurro chiaro, verde salvia, verde chiaro, giallo delicato e beige caldo.',
    respondBy: 'Si prega di rispondere entro il 01 maggio 2026',
    firstName: 'Nome',
    lastName: 'Cognome',
    email: 'Email',
    willYouAttend: 'Parteciperai al nostro matrimonio?',
    yesAttending: 'Sì, ci sarò!',
    noAttending: 'Purtroppo no',
    guests: 'Ospiti',
    addGuest: '+ Aggiungi Ospite',
    ageNote: '⚠️ Età solo per i bambini per favore!',
    guestName: 'Nome',
    guestAge: 'Età',
    stayQuestion: 'Avete già prenotato un alloggio?',
    stayYes: 'Sì, abbiamo già un alloggio',
    stayNo: 'No, abbiamo ancora bisogno di un consiglio',
    dietary: 'Quali sono le vostre o dei vostri familiari restrizioni dietetiche?',
    pleaseSelect: 'Seleziona',
    vegetarian: 'Vegetariano',
    vegan: 'Vegano',
    fish: 'Pesce',
    chicken: 'Pollo',
    allergies: 'Hai tu o qualche familiare allergie alimentari?',
    message: 'C\'è qualcos\'altro che vorresti dirci?',
    messagePlaceholder: 'Spero di vedervi presto...',
    submit: 'Invia Risposta',
    thankYou: 'Grazie!',
    receivedResponse: 'Abbiamo ricevuto la tua risposta 💕',
    gettingThere: 'Come Arrivare',
    byCar: '🚗 In Auto',
    parkingInfo: 'Il parcheggio è a pagamento e può essere prenotato tramite EasyPark.',
    tip: '💡 Suggerimento',
    tipText: 'Il centro storico - arrivare presto!',
    adminLogin: 'Login Admin',
    password: 'Password',
    login: 'Accedi',
    logout: 'Esci',
    wrongPassword: 'Password errata!',
    adminPanel: 'Pannello Admin',
    guestList: 'Lista Ospiti',
    totalGuests: 'Totale',
    confirmed: 'Confermati',
    declined: 'Rifiutati',
    exportCSV: 'Esporta come CSV',
    exportExcel: 'Esporta come Excel',
    delete: 'Elimina',
    timelineIntro: 'Joanna e Domenico festeggiano il loro matrimonio religioso l\'11 agosto 2026 a Trani – qui trovi il programma della giornata.',
    timelinePunctualNotice: 'Vi preghiamo di arrivare entro le 10:30: la cerimonia inizierà puntuale alle 10:30 e, dopo l\'arrivo della sposa, non sarà più possibile entrare in chiesa.',
    timelineCeremonyTitle: 'Cerimonia Religiosa',
    timelineCeremonyDesc: 'La cerimonia si svolge nella Cattedrale di Trani alle ore 10:30.',
    timelineArrivalTitle: 'Arrivo alla Tenuta',
    timelineArrivalDesc: 'Aperitivo di benvenuto per tutti gli ospiti.',
    timelineLunchTitle: 'Pranzo Tradizionale Pugliese',
    timelineLunchDesc: 'Le portate si alternano a momenti di ballo e intrattenimento.',
    timelineDessertTitle: 'Buffet di Dolci',
    timelineDessertDesc: 'Specialità pugliesi, napoletane e siciliane.',
    timelineCakeTitle: 'Torta Nuziale',
    timelineCakeDesc: 'Taglio della torta nuziale - un momento emozionante con gli ospiti.',
    timelinePartyTitle: 'After-Party al Lavinia Club',
    timelinePartyDesc: 'Festa con drink premium e musica in atmosfera unica.',
    submitRSVP: 'Invia RSVP',
    rsvpThankYou: 'Grazie!',
    rsvpThankYouMessage: 'La tua RSVP è stata inviata con successo. Non vediamo l\'ora di vederti!',
    faqTitle: 'Domande Frequenti',
    faqParkingQ: 'Ci sono parcheggi disponibili?',
    faqParkingA: 'Sì, a Trani ci sono parcheggi a pagamento prenotabili anche tramite EasyPark. Consigliamo di arrivare in anticipo per il centro storico.',
    faqKidsQ: 'I bambini sono invitati?',
    faqKidsA: 'Sì, i bambini sono i benvenuti. Indicateli per favore nell\'RSVP con nome ed età per aiutarci nell\'organizzazione.',
    faqGiftsQ: 'Cosa desiderate come regalo?',
    faqGiftsA: 'La vostra presenza è il nostro regalo più grande. Chi desidera comunque fare un pensiero può contribuire al nostro viaggio in Italia.',
    guestbook: 'Libro degli Ospiti',
    guestbookSubtitle: 'Lasciateci i vostri auguri',
    guestbookName: 'Il tuo nome',
    guestbookMessage: 'Il tuo messaggio',
    guestbookSubmit: 'Invia messaggio',
    guestbookThankYou: 'Grazie per le tue belle parole!',
    photos: 'Galleria',
    photosSubtitle: 'Condividete con noi i vostri momenti più belli del matrimonio',
    photosName: 'Il tuo nome',
    photosSelect: 'Seleziona e carica foto',
    photosUploading: 'Caricamento…',
    photosThankYou: 'Grazie per le vostre foto!',
    photosEmpty: 'Ancora nessuna foto – caricate le prime!',
    photosNameRequired: 'Inserisci prima il tuo nome.',
    photosUploadError: 'Caricamento non riuscito. Riprova.',
    photosHint: 'Le vostre foto appariranno qui nella galleria.',
    hotels: 'Raccomandazioni Hotel',
    hotelsSubtitle: 'I nostri consigli per il vostro soggiorno a Trani'
  },
  pl: {
    home: 'Strona główna',
    ourStory: 'Nasza historia',
    details: 'Szczegóły',
    rsvp: 'RSVP',
    location: 'Dojazd',
    areGettingMarried: 'Biorą ślub',
    untilBigDay: 'Do wielkiego dnia',
    days: 'Dni',
    hours: 'Godziny',
    minutes: 'Minuty',
    trani: 'Trani',
    whereOurLove: 'Miejsce, w którym nasza miłość zostanie oficjalnie przypieczętowana',
    loveStoryTitle: '💕 Nasza historia miłości',
    weddingDetails: 'Szczegóły ślubu',
    ceremony: 'Ceremonia',
    reception: 'Przyjęcie',
    dressCode: 'Dress code',
    dressCodeText: 'Prosimy o elegancki strój. Unikajcie bieli.',
    dressCodeColorPalette: 'Paleta kolorów: jasny błękit, zieleń szałwiowa, jasna zieleń, delikatna żółć i ciepły beż.',
    respondBy: 'Prosimy o odpowiedź do 1 maja 2026',
    firstName: 'Imię',
    lastName: 'Nazwisko',
    email: 'E-mail',
    willYouAttend: 'Czy będziesz uczestniczyć w naszym ślubie?',
    yesAttending: 'Tak, będę!',
    noAttending: 'Niestety nie',
    guests: 'Goście',
    addGuest: '+ Dodaj kolejnego gościa',
    ageNote: '⚠️ Wiek wpisujemy wyłącznie w przypadku dzieci!',
    guestName: 'Imię i nazwisko',
    guestAge: 'Wiek',
    stayQuestion: 'Czy macie już zarezerwowany nocleg?',
    stayYes: 'Tak, mamy już nocleg',
    stayNo: 'Nie, potrzebujemy jeszcze rekomendacji',
    dietary: 'Jakie są Twoje lub Twojej rodziny ograniczenia żywieniowe?',
    pleaseSelect: 'Wybierz opcję',
    vegetarian: 'Wegetariańskie',
    vegan: 'Wegańskie',
    fish: 'Ryba',
    chicken: 'Kurczak',
    allergies: 'Czy Ty lub ktoś z rodziny ma alergie pokarmowe?',
    message: 'Czy chcesz nam jeszcze coś przekazać?',
    messagePlaceholder: 'Mamy nadzieję, że się zobaczymy...',
    submit: 'Wyślij odpowiedź',
    thankYou: 'Dziękujemy!',
    receivedResponse: 'Otrzymaliśmy Twoją odpowiedź 💕',
    gettingThere: 'Dojazd',
    byCar: '🚗 Samochodem',
    parkingInfo: 'Parking jest płatny i można go zarezerwować m.in. przez EasyPark.',
    tip: '💡 Wskazówka',
    tipText: 'Stare miasto – warto przyjechać wcześniej!',
    adminLogin: 'Logowanie admina',
    password: 'Hasło',
    login: 'Zaloguj',
    logout: 'Wyloguj',
    wrongPassword: 'Nieprawidłowe hasło!',
    adminPanel: 'Panel administratora',
    guestList: 'Lista gości',
    totalGuests: 'Razem',
    confirmed: 'Potwierdzone',
    declined: 'Odmowy',
    exportCSV: 'Eksportuj do CSV',
    exportExcel: 'Eksportuj do Excela',
    delete: 'Usuń',
    timelineIntro: 'Joanna i Domenico świętują swój ślub kościelny 11 sierpnia 2026 roku w Trani – tutaj znajdziesz plan całego dnia.',
    timelinePunctualNotice: 'Prosimy o przybycie na 10:30. Ceremonia rozpoczyna się punktualnie o 10:30, a po przybyciu panny młodej nikt nie będzie już wpuszczany do kościoła.',
    timelineCeremonyTitle: 'Ceremonia Kościelna',
    timelineCeremonyDesc: 'Ceremonia odbywa się w Katedrze w Trani o godzinie 10:30.',
    timelineArrivalTitle: 'Przyjazd do Tenuty',
    timelineArrivalDesc: 'Aperitif powitalny dla wszystkich gości.',
    timelineLunchTitle: 'Tradycyjny Obiad Apulijski',
    timelineLunchDesc: 'Dania przeplatają się z momentami tańca i rozrywki.',
    timelineDessertTitle: 'Bufet Deserowy',
    timelineDessertDesc: 'Specjalności apulijskie, neapolitańskie i sycylijskie.',
    timelineCakeTitle: 'Tort Weselny',
    timelineCakeDesc: 'Krojenie tortu weselnego - emocjonalny moment z gośćmi.',
    timelinePartyTitle: 'After-Party w Lavinia Club',
    timelinePartyDesc: 'Zabawa z premium drinkami i muzyką w wyjątkowej atmosferze.',
    submitRSVP: 'Wyślij RSVP',
    rsvpThankYou: 'Dziękujemy!',
    rsvpThankYouMessage: 'Twoja RSVP została pomyślnie wysłana. Cieszymy się na spotkanie!',
    faqTitle: 'Najczęściej zadawane pytania',
    faqParkingQ: 'Czy są dostępne miejsca parkingowe?',
    faqParkingA: 'Tak, w Trani dostępne są płatne parkingi, które można zarezerwować m.in. przez EasyPark. Ze względu na stare miasto polecamy przyjechać wcześniej.',
    faqKidsQ: 'Czy dzieci są zaproszone?',
    faqKidsA: 'Tak, dzieci są mile widziane. Prosimy wpisać je w formularzu RSVP z imieniem i wiekiem, abyśmy mogli wszystko zaplanować.',
    faqGiftsQ: 'Co chcielibyście dostać w prezencie?',
    faqGiftsA: 'Wasza obecność jest dla nas najpiękniejszym prezentem. Jeśli jednak ktoś chciałby nas dodatkowo obdarować, ucieszymy się z wkładu w naszą podróż do Włoch.',
    guestbook: 'Księga Gości',
    guestbookSubtitle: 'Zostawcie nam swoje życzenia',
    guestbookName: 'Twoje imię',
    guestbookMessage: 'Twoja wiadomość',
    guestbookSubmit: 'Wyślij wiadomość',
    guestbookThankYou: 'Dziękujemy za miłe słowa!',
    photos: 'Galeria',
    photosSubtitle: 'Podzielcie się z nami najpiękniejszymi chwilami z wesela',
    photosName: 'Twoje imię',
    photosSelect: 'Wybierz i prześlij zdjęcia',
    photosUploading: 'Przesyłanie…',
    photosThankYou: 'Dziękujemy za wasze zdjęcia!',
    photosEmpty: 'Jeszcze brak zdjęć – prześlijcie pierwsze!',
    photosNameRequired: 'Najpierw podaj swoje imię.',
    photosUploadError: 'Przesyłanie nie powiodło się. Spróbuj ponownie.',
    photosHint: 'Wasze zdjęcia pojawią się tutaj w galerii.',
    hotels: 'Rekomendacje Hoteli',
    hotelsSubtitle: 'Nasze rekomendacje na pobyt w Trani'
  }
};

const backofficeTranslations = {
  de: {
    roleAdmin: 'Admin',
    roleVenue: 'Location',
    venuePanel: 'Location Planung',
    venueLogin: 'Location Login',
    venueAccessLink: 'Accesso Location / Tenuta',
    venueAccessTitle: 'Location-Zugang',
    venueAccessSubtitle: 'Für Tenuta und Organisation vor Ort. Zugriff auf Gästeliste und Sitzplan.',
    venueAccessHint: 'Mit dem Firebase-Login für die Location anmelden.',
    guestsTab: '👥 Gäste',
    seatingTab: '🪑 Sitzplan',
    filter: 'Filter',
    all: 'Alle',
    onlyConfirmed: 'Nur Zusagen',
    onlyDeclined: 'Nur Absagen',
    adults: 'Erwachsene',
    children: 'Kinder',
    babies: 'Babys',
    allGuestsNote: 'Alle Gäste',
    confirmedNote: 'Zugesagt',
    declinedNote: 'Abgesagt',
    csvVenueNotice: 'CSV ohne private Kontaktdaten',
    status: 'Status',
    guestCompanions: 'Begleitpersonen',
    dietary: 'Diät',
    allergies: 'Allergien',
    date: 'Datum',
    actions: 'Aktionen',
    accommodation: 'Unterkunft',
    message: 'Nachricht',
    email: 'E-Mail',
    accepted: 'Zusage',
    declined: 'Absage',
    roomPlan: 'Raumplan',
    addTable: 'Neuen Tisch hinzufügen',
    tableNamePlaceholder: 'Tischname (z.B. Tisch 1, Brauttisch)',
    seats: 'Plätze',
    createTable: '+ Tisch erstellen',
    noGuestsAssigned: 'Keine Gäste',
    spots: 'Plätze',
    free: 'frei',
    full: 'Voll',
    addGuestToTable: '+ Gast hinzufügen...',
    deleteTable: '🗑️ Tisch löschen',
    noTablesYet: 'Noch keine Tische erstellt.',
    createFirstTable: 'Erstelle oben den ersten Tisch.',
    summary: '📊 Übersicht',
    tables: 'Tische',
    seated: 'Platziert',
    totalSeats: 'Gesamtplätze',
    unseated: 'Noch zu platzieren',
    tableFull: 'Dieser Tisch ist bereits voll.',
    savePositionError: 'Fehler beim Speichern der Tischposition',
    moveGuestError: 'Fehler beim Verschieben des Gastes',
    guestOverviewTitle: 'Gästeliste',
    venueGuestPrivacy: 'Kontaktdaten, Unterkunft und private Nachrichten bleiben verborgen.'
  },
  it: {
    roleAdmin: 'Admin',
    roleVenue: 'Location',
    venuePanel: 'Pianificazione Location',
    venueLogin: 'Accesso Location',
    venueAccessLink: 'Accesso Location / Tenuta',
    venueAccessTitle: 'Accesso Location',
    venueAccessSubtitle: 'Per la tenuta e l\'organizzazione sul posto. Accesso alla lista ospiti e al tableau.',
    venueAccessHint: 'Accedi con il login Firebase della location.',
    guestsTab: '👥 Ospiti',
    seatingTab: '🪑 Tableau',
    filter: 'Filtro',
    all: 'Tutti',
    onlyConfirmed: 'Solo confermati',
    onlyDeclined: 'Solo rifiutati',
    adults: 'Adulti',
    children: 'Bambini',
    babies: 'Neonati',
    allGuestsNote: 'Tutti gli ospiti',
    confirmedNote: 'Confermati',
    declinedNote: 'Rifiutati',
    csvVenueNotice: 'CSV senza dati di contatto privati',
    status: 'Stato',
    guestCompanions: 'Accompagnatori',
    dietary: 'Dieta',
    allergies: 'Allergie',
    date: 'Data',
    actions: 'Azioni',
    accommodation: 'Alloggio',
    message: 'Messaggio',
    email: 'Email',
    accepted: 'Confermato',
    declined: 'Rifiutato',
    roomPlan: 'Planimetria',
    addTable: 'Aggiungi un tavolo',
    tableNamePlaceholder: 'Nome tavolo (es. Tavolo 1, tavolo sposi)',
    seats: 'Posti',
    createTable: '+ Crea tavolo',
    noGuestsAssigned: 'Nessun ospite',
    spots: 'Posti',
    free: 'liberi',
    full: 'Completo',
    addGuestToTable: '+ Aggiungi ospite...',
    deleteTable: '🗑️ Elimina tavolo',
    noTablesYet: 'Nessun tavolo creato finora.',
    createFirstTable: 'Crea sopra il primo tavolo.',
    summary: '📊 Riepilogo',
    tables: 'Tavoli',
    seated: 'Assegnati',
    totalSeats: 'Posti totali',
    unseated: 'Da assegnare',
    tableFull: 'Questo tavolo è già completo.',
    savePositionError: 'Errore durante il salvataggio della posizione del tavolo',
    moveGuestError: 'Errore durante lo spostamento dell\'ospite',
    guestOverviewTitle: 'Lista ospiti',
    venueGuestPrivacy: 'I dati di contatto, l\'alloggio e i messaggi privati restano nascosti.'
  },
  en: {
    roleAdmin: 'Admin',
    roleVenue: 'Venue',
    venuePanel: 'Venue Planning',
    venueLogin: 'Venue Login',
    venueAccessLink: 'Venue / Tenuta Access',
    venueAccessTitle: 'Venue Access',
    venueAccessSubtitle: 'For the venue team and on-site organization. Access to guest list and seating plan.',
    venueAccessHint: 'Sign in with the venue Firebase account.',
    guestsTab: '👥 Guests',
    seatingTab: '🪑 Seating',
    filter: 'Filter',
    all: 'All',
    onlyConfirmed: 'Confirmed only',
    onlyDeclined: 'Declined only',
    adults: 'Adults',
    children: 'Children',
    babies: 'Babies',
    allGuestsNote: 'All guests',
    confirmedNote: 'Confirmed',
    declinedNote: 'Declined',
    csvVenueNotice: 'CSV without private contact data',
    status: 'Status',
    guestCompanions: 'Companions',
    dietary: 'Dietary',
    allergies: 'Allergies',
    date: 'Date',
    actions: 'Actions',
    accommodation: 'Accommodation',
    message: 'Message',
    email: 'Email',
    accepted: 'Confirmed',
    declined: 'Declined',
    roomPlan: 'Room Plan',
    addTable: 'Add table',
    tableNamePlaceholder: 'Table name (e.g. Table 1, head table)',
    seats: 'Seats',
    createTable: '+ Create table',
    noGuestsAssigned: 'No guests',
    spots: 'seats',
    free: 'free',
    full: 'Full',
    addGuestToTable: '+ Add guest...',
    deleteTable: '🗑️ Delete table',
    noTablesYet: 'No tables created yet.',
    createFirstTable: 'Create the first table above.',
    summary: '📊 Summary',
    tables: 'Tables',
    seated: 'Seated',
    totalSeats: 'Total seats',
    unseated: 'Unseated',
    tableFull: 'This table is already full.',
    savePositionError: 'Error saving table position',
    moveGuestError: 'Error moving guest',
    guestOverviewTitle: 'Guest list',
    venueGuestPrivacy: 'Contact details, accommodation and private notes stay hidden.'
  },
  pl: {
    roleAdmin: 'Admin',
    roleVenue: 'Lokal',
    venuePanel: 'Planowanie Lokalu',
    venueLogin: 'Logowanie lokalu',
    venueAccessLink: 'Dostęp Location / Tenuta',
    venueAccessTitle: 'Dostęp lokalu',
    venueAccessSubtitle: 'Dla lokalu i organizacji na miejscu. Dostęp do listy gości i planu stołów.',
    venueAccessHint: 'Zaloguj się kontem Firebase lokalu.',
    guestsTab: '👥 Goście',
    seatingTab: '🪑 Plan stołów',
    filter: 'Filtr',
    all: 'Wszyscy',
    onlyConfirmed: 'Tylko potwierdzeni',
    onlyDeclined: 'Tylko odmowy',
    adults: 'Dorośli',
    children: 'Dzieci',
    babies: 'Niemowlęta',
    allGuestsNote: 'Wszyscy goście',
    confirmedNote: 'Potwierdzeni',
    declinedNote: 'Odmowy',
    csvVenueNotice: 'CSV bez prywatnych danych kontaktowych',
    status: 'Status',
    guestCompanions: 'Osoby towarzyszące',
    dietary: 'Dieta',
    allergies: 'Alergie',
    date: 'Data',
    actions: 'Akcje',
    accommodation: 'Nocleg',
    message: 'Wiadomość',
    email: 'E-mail',
    accepted: 'Potwierdzony',
    declined: 'Odmowa',
    roomPlan: 'Plan sali',
    addTable: 'Dodaj stół',
    tableNamePlaceholder: 'Nazwa stołu (np. Stół 1, stół pary młodej)',
    seats: 'Miejsca',
    createTable: '+ Utwórz stół',
    noGuestsAssigned: 'Brak gości',
    spots: 'miejsca',
    free: 'wolne',
    full: 'Pełny',
    addGuestToTable: '+ Dodaj gościa...',
    deleteTable: '🗑️ Usuń stół',
    noTablesYet: 'Nie utworzono jeszcze stołów.',
    createFirstTable: 'Utwórz pierwszy stół powyżej.',
    summary: '📊 Podsumowanie',
    tables: 'Stoły',
    seated: 'Posadzeni',
    totalSeats: 'Łącznie miejsc',
    unseated: 'Do posadzenia',
    tableFull: 'Ten stół jest już pełny.',
    savePositionError: 'Błąd podczas zapisu pozycji stołu',
    moveGuestError: 'Błąd podczas przenoszenia gościa',
    guestOverviewTitle: 'Lista gości',
    venueGuestPrivacy: 'Dane kontaktowe, nocleg i prywatne wiadomości pozostają ukryte.'
  }
};

function WeddingOnePager() {
  const [language, setLanguage] = useState(() => {
    const search = new URLSearchParams(window.location.search);
    const isVenueRoute = search.get('venue') === 'login';
    const browserLang = navigator.language || navigator.languages?.[0] || 'en';
    if (isVenueRoute) {
      if (browserLang.startsWith('de')) return 'de';
      return 'it';
    }
    if (browserLang.startsWith('de')) return 'de';
    if (browserLang.startsWith('it')) return 'it';
    if (browserLang.startsWith('pl')) return 'pl';
    return 'en';
  });
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [isVenue, setIsVenue] = useState(false);
  const [isPlanner, setIsPlanner] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [loginMode, setLoginMode] = useState('admin');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');
  const [guests, setGuests] = useState([]);
  const [adminTodos, setAdminTodos] = useState([]);
  const [newTodo, setNewTodo] = useState({ title: '', category: '', status: 'open', note: '' });
  const [adminBudget, setAdminBudget] = useState([]);
  const [newBudgetItem, setNewBudgetItem] = useState({ title: '', category: '', planned: '', actual: '', status: 'open' });
  const [vendors, setVendors] = useState([]);
  const [newVendor, setNewVendor] = useState({ 
    name: '', 
    category: 'location', 
    contact: '', 
    phone: '', 
    email: '',
    offer: '', 
    status: 'anfrage', 
    notes: '',
    rating: 0
  });
  const [adminView, setAdminView] = useState('guests'); // 'guests', 'vendors', 'budget', 'todos', 'checklist', 'seating'
  const [tables, setTables] = useState([]);
  
  // Handle table dragging for both mouse and touch events
  const startDragging = (startEvent, table, startLeft, startTop) => {
    const container = document.querySelector('.bg-neutral-950\\/70 .relative.w-full.h-[480px]');
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const startX = startEvent.clientX !== undefined ? startEvent.clientX : (startEvent.pageX || 0);
    const startY = startEvent.clientY !== undefined ? startEvent.clientY : (startEvent.pageY || 0);
    
    const handleMove = (moveEvent) => {
      const currentX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
      const currentY = moveEvent.clientY || (moveEvent.touches && moveEvent.touches[0].clientY);
      if (currentX === undefined || currentY === undefined) return;
      
      const deltaXPercent = ((currentX - startX) / rect.width) * 100;
      const deltaYPercent = ((currentY - startY) / rect.height) * 100;
      const newLeft = Math.min(95, Math.max(5, startLeft + deltaXPercent));
      const newTop = Math.min(95, Math.max(5, startTop + deltaYPercent));
      
      setTables(prev =>
        prev.map(t =>
          t.id === table.id ? { ...t, posX: newLeft, posY: newTop } : t
        )
      );
    };

    const handleUp = async (upEvent) => {
      // Prevent any default behavior that might interfere with the touch events
      upEvent.preventDefault();
      
      // Remove both mouse and touch event listeners
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
      
      // Update the final position in the database
      const currentX = upEvent.clientX || (upEvent.changedTouches && upEvent.changedTouches[0].clientX);
      const currentY = upEvent.clientY || (upEvent.changedTouches && upEvent.changedTouches[0].clientY);
      
      if (currentX !== undefined && currentY !== undefined) {
        const deltaXPercent = ((currentX - startX) / rect.width) * 100;
        const deltaYPercent = ((currentY - startY) / rect.height) * 100;
        const finalLeft = Math.min(95, Math.max(5, startLeft + deltaXPercent));
        const finalTop = Math.min(95, Math.max(5, startTop + deltaYPercent));
        
        try {
          await updateDoc(doc(db, 'wedding_tables', table.id), {
            posX: finalLeft,
            posY: finalTop
          });
        } catch (error) {
          console.error(bt.savePositionError, error);
        }
      }
    };

    // Add both mouse and touch event listeners
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchend', handleUp, { passive: false });
  };

  // Handle table resizing for both mouse and touch events
  const startResizing = (startEvent, table) => {
    const startX = startEvent.clientX !== undefined ? startEvent.clientX : (startEvent.pageX || 0);
    const startY = startEvent.clientY !== undefined ? startEvent.clientY : (startEvent.pageY || 0);
    const startWidth = typeof table.width === 'number' ? table.width : 160;
    const startHeight = typeof table.height === 'number' ? table.height : 100;

    const handleMove = (moveEvent) => {
      const currentX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
      const currentY = moveEvent.clientY || (moveEvent.touches && moveEvent.touches[0].clientY);
      if (currentX === undefined || currentY === undefined) return;
      
      const deltaX = currentX - startX;
      const deltaY = currentY - startY;
      const newWidth = Math.max(80, startWidth + deltaX);
      const newHeight = Math.max(50, startHeight + deltaY);
      
      setTables(prev => prev.map(t => 
        t.id === table.id ? { ...t, width: newWidth, height: newHeight } : t
      ));
    };

    const handleUp = async (upEvent) => {
      // Prevent any default behavior
      upEvent.preventDefault();
      
      // Remove both mouse and touch event listeners
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
      
      // Update the final size in the database
      const currentX = upEvent.clientX || (upEvent.changedTouches && upEvent.changedTouches[0].clientX);
      const currentY = upEvent.clientY || (upEvent.changedTouches && upEvent.changedTouches[0].clientY);
      
      if (currentX !== undefined && currentY !== undefined) {
        const deltaX = currentX - startX;
        const deltaY = currentY - startY;
        const finalWidth = Math.max(80, startWidth + deltaX);
        const finalHeight = Math.max(50, startHeight + deltaY);
        
        try {
          await updateDoc(doc(db, 'wedding_tables', table.id), {
            width: finalWidth,
            height: finalHeight
          });
        } catch (error) {
          console.error('Fehler beim Speichern der Tischgröße', error);
        }
      }
    };

    // Add both mouse and touch event listeners
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchend', handleUp, { passive: false });
  };
  const [newTable, setNewTable] = useState({ name: '', seats: 8 });
  const [checklistItems, setChecklistItems] = useState([]);
  const [guestFilter, setGuestFilter] = useState('all'); // 'all' | 'attending' | 'declined'
  const [guestbookMessages, setGuestbookMessages] = useState([]);
  const [guestbookIndex, setGuestbookIndex] = useState(0);
  const [guestbookForm, setGuestbookForm] = useState({ name: '', message: '' });
  const [guestbookSubmitted, setGuestbookSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoName, setPhotoName] = useState('');
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadProgress, setPhotoUploadProgress] = useState('');
  const [photoUploadDone, setPhotoUploadDone] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [fullImages, setFullImages] = useState({});
  const [zipDownloading, setZipDownloading] = useState(false);
  const [zipProgress, setZipProgress] = useState('');
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isVisible, setIsVisible] = useState({});
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [rsvpForm, setRsvpForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    attending: null,
    hasAccommodation: null,
    guests: [{ name: '', age: '' }],
    dietaryRestrictions: '',
    allergies: '',
    message: ''
  });
  const [showRsvpPopup, setShowRsvpPopup] = useState(false);
  const [guestId, setGuestId] = useState(() => {
    return localStorage.getItem('wedding_guest_id') || null;
  });
  
  // Zugangsschutz - Token oder Passwort
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if already authenticated in session
    if (sessionStorage.getItem('wedding_access') === 'granted') {
      return true;
    }
    
    // Check for URL token (QR / Direktlink – nicht das Passwort, damit es nicht in der URL steht)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;
    
    if (ACCESS_TOKEN && token === ACCESS_TOKEN) {
      sessionStorage.setItem('wedding_access', 'granted');
      // Clean URL (remove token from URL) – Hash (#photos) für Galerie-Sprung behalten
      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
      return true;
    }

    return false;
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  // Token aus URL beim Laden prüfen (z. B. nach QR-Scan) – Zugang ohne Passwort in der URL
  useEffect(() => {
    if (isAuthenticated) return;
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;
    if (ACCESS_TOKEN && token === ACCESS_TOKEN) {
      sessionStorage.setItem('wedding_access', 'granted');
      setPasswordError(false);
      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
      setIsAuthenticated(true);
    }
  }, [isAuthenticated]);

  // Nach QR-Scan mit #photos in der URL direkt zur Galerie springen
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    if (window.location.hash !== '#photos') return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById('photos');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 600);
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  const homeRef = useRef(null);
  const storyRef = useRef(null);
  const detailsRef = useRef(null);
  const rsvpRef = useRef(null);
  const guestbookRef = useRef(null);
  const photosSectionRef = useRef(null);
  const photoInputRef = useRef(null);
  const hotelsRef = useRef(null);
  const locationRef = useRef(null);

  const t = translations[language];
  const bt = backofficeTranslations[language] || backofficeTranslations.de;
  const isVenueLoginRoute = new URLSearchParams(window.location.search).get('venue') === 'login';
  const hasVenueAccess = isVenue || isPlanner;
  const hasBackofficeAccess = isAdmin || hasVenueAccess;
  const isVenueOnly = hasVenueAccess && !isAdmin;
  const canViewPrivateGuestData = isAdmin;
  const canDeleteGuests = isAdmin;
  const canManageAdminCollections = isAdmin;
  const backofficeTabs = isVenueOnly
    ? [
        { id: 'guests', label: bt.guestsTab },
        { id: 'seating', label: bt.seatingTab }
      ]
    : [
        { id: 'guests', label: '👥 Gäste' },
        { id: 'vendors', label: '🏪 Dienstleister' },
        { id: 'guestbook', label: '📖 Gästebuch' },
        { id: 'photos', label: '📸 Fotos' },
        { id: 'checklist', label: '📋 Checklist' },
        { id: 'seating', label: '🪑 Sitzplan' },
        { id: 'budget', label: '💰 Budget' },
        { id: 'todos', label: '✅ Aufgaben' },
        { id: 'qrcode', label: '📱 QR-Code Einladung' }
      ];

  const TODO_PRESETS = [
    { title: 'Videograf buchen', category: 'Dienstleister' },
    { title: 'Fotograf buchen', category: 'Dienstleister' },
    { title: 'DJ / Band buchen', category: 'Musik' },
    { title: 'Location final bestätigen', category: 'Location' },
    { title: 'Dekoration planen & bestellen', category: 'Deko' },
    { title: 'Papeterie (Einladungen, Menükarten)', category: 'Papeterie' },
    { title: 'Brautkleid / Anzug finalisieren', category: 'Outfits' },
    { title: 'Ringe bestellen', category: 'Ringe' },
    { title: 'Friseur & Make-up buchen', category: 'Beauty' },
    { title: 'Flüge buchen', category: 'Reise' },
    { title: 'Unterkünfte für Gäste koordinieren', category: 'Unterkunft' },
    { title: 'Pfarrer / Trauredner abstimmen', category: 'Zeremonie' },
    { title: 'Hochzeitstorte bestellen', category: 'Torte' },
    { title: 'Sitzplan erstellen', category: 'Organisation' },
    { title: 'Ablaufplan finalisieren', category: 'Organisation' },
    { title: 'Dankeskarten planen', category: 'Papeterie' }
  ];

  // Check Admin Login via Firebase Auth
  useEffect(() => {
    if (!auth) {
      setIsAdmin(false);
      setIsVenue(false);
      setIsPlanner(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAdmin(false);
        setIsVenue(false);
        setIsPlanner(false);
        return;
      }

      try {
        const tokenResult = await user.getIdTokenResult();
        const isAdminClaim = !!tokenResult.claims.isAdmin;
        const isVenueClaim = !!tokenResult.claims.isVenue;
        const isPlannerClaim = !!tokenResult.claims.isPlanner;
        setIsAdmin(isAdminClaim);
        setIsVenue(isVenueClaim);
        setIsPlanner(isPlannerClaim);
      } catch (error) {
        setIsAdmin(false);
        setIsVenue(false);
        setIsPlanner(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Guestbook Carousel Auto-Advance
  useEffect(() => {
    if (!guestbookMessages || guestbookMessages.length === 0) return;
    // Reset index if current index out of bounds
    if (guestbookIndex >= guestbookMessages.length) {
      setGuestbookIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setGuestbookIndex((prev) => {
        if (guestbookMessages.length === 0) return 0;
        return (prev + 1) % guestbookMessages.length;
      });
    }, 6000);
    return () => clearInterval(interval);
  }, [guestbookMessages, guestbookIndex]);

  // Load admin-only data only after admin auth is confirmed.
  useEffect(() => {
    if (!db || !hasBackofficeAccess) {
      setGuests([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(collection(db, 'guests'), (snapshot) => {
      setGuests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [hasBackofficeAccess]);

  // Load Guestbook Messages from Firebase
  useEffect(() => {
    if (!db) {
      setGuestbookMessages([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(collection(db, 'guestbook'),
      (snapshot) => {
        const messages = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setGuestbookMessages(messages);
      }
    );
    return () => unsubscribe();
  }, []);

  // Load Photo Gallery (nur Thumbnails; Vollbilder werden erst in der Lightbox geladen)
  useEffect(() => {
    if (!db) {
      setPhotos([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(collection(db, 'photos'),
      (snapshot) => {
        const items = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPhotos(items);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!db || !canManageAdminCollections) {
      setAdminTodos([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'wedding_todos'),
      (snapshot) => {
        const todos = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setAdminTodos(todos);
      }
    );

    return () => unsubscribe();
  }, [canManageAdminCollections]);

  useEffect(() => {
    if (!db || !canManageAdminCollections) {
      setAdminBudget([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'wedding_budget'),
      (snapshot) => {
        const items = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setAdminBudget(items);
      }
    );

    return () => unsubscribe();
  }, [canManageAdminCollections]);

  // Load Vendors from Firebase
  useEffect(() => {
    if (!db || !canManageAdminCollections) {
      setVendors([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'wedding_vendors'),
      (snapshot) => {
        const items = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.category || '').localeCompare(b.category || ''));
        setVendors(items);
      }
    );

    return () => unsubscribe();
  }, [canManageAdminCollections]);

  // Load Tables from Firebase
  useEffect(() => {
    if (!db || !hasBackofficeAccess) {
      setTables([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'wedding_tables'),
      (snapshot) => {
        const items = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setTables(items);
      }
    );

    return () => unsubscribe();
  }, [hasBackofficeAccess]);

  // Load Checklist from Firebase
  useEffect(() => {
    if (!db || !canManageAdminCollections) {
      setChecklistItems([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'wedding_checklist'),
      (snapshot) => {
        const items = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.monthsBefore || 0) - (b.monthsBefore || 0));
        setChecklistItems(items);
      }
    );

    return () => unsubscribe();
  }, [canManageAdminCollections]);

  // Countdown Timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const difference = WEDDING_DATE - now;
      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60)
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Scroll Observer
  useEffect(() => {
    const initialVisibility = {
      home: true,
      story: false,
      details: false,
      rsvp: false,
      guestbook: false,
      photos: false,
      hotels: false,
      location: false
    };
    setIsVisible(initialVisibility);

    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
      setIsVisible({
        home: true,
        story: true,
        details: true,
        rsvp: true,
        guestbook: true,
        photos: true,
        hotels: true,
        location: true
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
            setActiveSection(entry.target.id);
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '50px 0px -50px 0px' // Triggert früher
      }
    );

    [homeRef, storyRef, detailsRef, rsvpRef, guestbookRef, photosSectionRef, hotelsRef, locationRef].forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    // Fallback: Nach 2 Sekunden alle Sektionen sichtbar machen
    const fallbackTimer = setTimeout(() => {
      setIsVisible({
        home: true,
        story: true,
        details: true,
        rsvp: true,
        guestbook: true,
        photos: true,
        hotels: true,
        location: true
      });
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, []);

  const addTodo = async () => {
    if (!newTodo.title.trim()) return;
    try {
      await addDoc(collection(db, 'wedding_todos'), {
        ...newTodo,
        createdAt: new Date().toISOString()
      });
      setNewTodo({ title: '', category: '', status: 'open', note: '' });
    } catch (error) {
      alert('ToDo konnte nicht gespeichert werden');
    }
  };

  const toggleTodoStatus = async (todo) => {
    const nextStatus =
      todo.status === 'open'
        ? 'in_progress'
        : todo.status === 'in_progress'
          ? 'done'
          : 'open';
    try {
      await updateDoc(doc(db, 'wedding_todos', todo.id), { status: nextStatus });
    } catch (error) {
      alert('Status konnte nicht aktualisiert werden');
    }
  };

  const deleteTodo = async (id) => {
    if (!window.confirm('ToDo wirklich löschen?')) return;
    try {
      await deleteDoc(doc(db, 'wedding_todos', id));
    } catch (error) {
      alert('ToDo konnte nicht gelöscht werden');
    }
  };

  const addBudgetItem = async () => {
    if (!newBudgetItem.title.trim()) return;
    try {
      await addDoc(collection(db, 'wedding_budget'), {
        ...newBudgetItem,
        planned: Number(newBudgetItem.planned) || 0,
        actual: Number(newBudgetItem.actual) || 0,
        createdAt: new Date().toISOString()
      });
      setNewBudgetItem({ title: '', category: '', planned: '', actual: '', status: 'open' });
    } catch (error) {
      alert('Budgeteintrag konnte nicht gespeichert werden');
    }
  };

  const updateBudgetStatus = async (item, status) => {
    try {
      await updateDoc(doc(db, 'wedding_budget', item.id), { status });
    } catch (error) {
      alert('Status konnte nicht aktualisiert werden');
    }
  };

  const deleteBudgetItem = async (id) => {
    if (!window.confirm('Budgeteintrag wirklich löschen?')) return;
    try {
      await deleteDoc(doc(db, 'wedding_budget', id));
    } catch (error) {
      alert('Budgeteintrag konnte nicht gelöscht werden');
    }
  };

  // Parallax Scroll Effect
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setScrollY(currentY);

      // Parallax für Love Story Hintergrund (nur Desktop)
      if (window.innerWidth > 0) {
        const loveStoryBg = document.getElementById('loveStoryBg');
        const storySection = document.getElementById('story');

        if (loveStoryBg && storySection) {
          const rect = storySection.getBoundingClientRect();
          const sectionTop = rect.top + window.scrollY;
          const offsetInsideSection = currentY - sectionTop;

          // Erst bewegen, wenn man in die Section hinein scrollt
          if (offsetInsideSection > 0) {
            const rate = offsetInsideSection * 0.3;
            loveStoryBg.style.transform = `translate3d(0, ${rate}px, 0)`;
          } else {
            // Am Anfang fix, kein Versatz nach unten
            loveStoryBg.style.transform = 'translate3d(0, 0, 0)';
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navbar hide/show on scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY > lastScrollY + 5) {
        setShowNavbar(false);
      } else if (currentY < lastScrollY - 5) {
        setShowNavbar(true);
      }
      setLastScrollY(currentY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const handleAdminLogin = async (mode = loginMode) => {
    if (!auth) {
      setAdminError(language === 'it' ? 'Configurazione Firebase locale mancante' : 'Lokale Firebase-Konfiguration fehlt');
      return;
    }

    try {
      const cred = await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
      const tokenResult = await cred.user.getIdTokenResult();
      const isAdminClaim = !!tokenResult.claims.isAdmin;
      const isVenueClaim = !!tokenResult.claims.isVenue;
      const isPlannerClaim = !!tokenResult.claims.isPlanner;
      const canEnterVenue = isAdminClaim || isVenueClaim || isPlannerClaim;

      if (mode === 'venue' ? canEnterVenue : isAdminClaim) {
        setIsAdmin(isAdminClaim);
        setIsVenue(isVenueClaim);
        setIsPlanner(isPlannerClaim);
        setShowAdminLogin(false);
        setAdminError('');
        setAdminPassword('');
        setAdminView('guests');
      } else {
        setAdminError(mode === 'venue' ? 'Kein Location-/Planungs-Konto' : 'Kein Admin-Konto');
        await signOut(auth);
      }
    } catch (error) {
      setAdminError('Login fehlgeschlagen');
    }
  };

  const handleAdminLogout = async () => {
    if (!auth) {
      setIsAdmin(false);
      setIsVenue(false);
      setIsPlanner(false);
      setAdminView('guests');
      return;
    }

    await signOut(auth);
    setIsAdmin(false);
    setIsVenue(false);
    setIsPlanner(false);
    setAdminView('guests');
  };

  const sendNotification = async (guestData) => {
    try {
      const message = `🎉 Neue RSVP eingegangen!

` +
        `👤 Name: ${guestData.firstName} ${guestData.lastName}
` +
        `📧 E-Mail: ${guestData.email}
` +
        `✅ Status: ${guestData.attending ? 'ZUSAGE ✅' : 'ABSAGE ❌'}
` +
        `👥 Gäste: ${guestData.guests.filter(g => g.name).length}
` +
        `🏨 Unterkunft: ${guestData.attending ? (guestData.hasAccommodation === true ? 'Vorhanden' : guestData.hasAccommodation === false ? 'Benötigt' : 'Unbekannt') : 'N/A'}
` +
        `💬 Nachricht: ${guestData.message || 'Keine'}

` +
        `🔗 Admin-Panel: weddingmoments.j-beauty0711.de`;

      // Option 1: Telegram Bot
      const telegramToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
      const telegramChatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;
      
      if (telegramToken && telegramChatId) {
        const telegramUrl = `https://api.telegram.org/bot${telegramToken}/sendMessage`;
        await fetch(telegramUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: telegramChatId,
            text: message,
            parse_mode: 'HTML'
          })
        });
        console.log('📱 Telegram-Benachrichtigung gesendet!');
        return;
      }

      // Option 2: Discord Webhook
      const discordWebhook = process.env.REACT_APP_DISCORD_WEBHOOK;
      
      if (discordWebhook) {
        await fetch(discordWebhook, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: message,
            username: 'Hochzeits-Bot'
          })
        });
        console.log('📱 Discord-Benachrichtigung gesendet!');
        return;
      }

      // Option 3: CallMeBot (Fallback)
      const phoneNumber = process.env.REACT_APP_WHATSAPP_NUMBER;
      const apiKey = process.env.REACT_APP_CALLMEBOT_API_KEY;
      
      if (phoneNumber && apiKey) {
        const encodedMessage = encodeURIComponent(message);
        const url = `https://api.callmebot.com/whatsapp.php?phone=${phoneNumber}&text=${encodedMessage}&apikey=${apiKey}`;
        
        await fetch(url, { method: 'GET' });
        console.log('📱 WhatsApp-Benachrichtigung gesendet!');
      }
    } catch (error) {
      console.error('❌ Benachrichtigung fehlgeschlagen:', error);
    }
  };

  const handleRSVPSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(rsvpForm.email)) {
      alert('Bitte eine gültige E-Mail-Adresse eingeben');
      return;
    }

    if (rsvpForm.firstName && rsvpForm.lastName && rsvpForm.email && rsvpForm.attending !== null) {
      try {
        // 1. RSVP in Firebase speichern
        const docRef = await addDoc(collection(db, 'guests'), {
          ...rsvpForm,
          createdAt: new Date().toISOString()
        });
        setGuestId(docRef.id);
        localStorage.setItem('wedding_guest_id', docRef.id);
        
        // 2. Benachrichtigung senden
        await sendNotification(rsvpForm);
        
        // 3. Popup anzeigen
        setShowRsvpPopup(true);
        
        // 4. Formular zurücksetzen
        setRsvpForm({
          firstName: '',
          lastName: '',
          email: '',
          attending: null,
          hasAccommodation: null,
          guests: [{ name: '', age: '' }],
          dietaryRestrictions: '',
          allergies: '',
          message: ''
        });
        
        // 5. Popup nach 4 Sekunden ausblenden
        setTimeout(() => {
          setShowRsvpPopup(false);
        }, 4000);
      } catch (error) {
        alert('Fehler beim Speichern');
      }
    } else {
      alert('Bitte alle Pflichtfelder ausfüllen');
    }
  };

  const handleGuestbookSubmit = async () => {
    if (!guestId) {
      alert('Bitte zuerst die RSVP ausfüllen, bevor du ins Gästebuch schreibst.');
      return;
    }

    if (guestbookForm.name && guestbookForm.message) {
      try {
        await addDoc(collection(db, 'guestbook'), {
          ...guestbookForm,
          guestId,
          createdAt: new Date().toISOString()
        });
        setGuestbookSubmitted(true);
        setTimeout(() => {
          setGuestbookForm({ name: '', message: '' });
          setGuestbookSubmitted(false);
        }, 3000);
      } catch (error) {
        alert('Fehler beim Speichern');
      }
    } else {
      alert('Bitte alle Felder ausfüllen');
    }
  };

  // Bild client-seitig verkleinern und als JPEG-DataURL zurückgeben
  const compressImage = (file, maxDim, quality) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Bild konnte nicht gelesen werden'));
    };
    img.src = url;
  });

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (!photoName.trim()) {
      alert(t.photosNameRequired);
      e.target.value = '';
      return;
    }

    setPhotoUploading(true);
    let uploaded = 0;
    for (let i = 0; i < files.length; i++) {
      setPhotoUploadProgress(`${i + 1} / ${files.length}`);
      try {
        const thumb = await compressImage(files[i], 400, 0.6);
        // Firestore-Dokumentlimit ist 1 MiB → notfalls stärker komprimieren
        let full = await compressImage(files[i], 1280, 0.75);
        if (full.length > 900000) full = await compressImage(files[i], 1024, 0.6);
        if (full.length > 900000) full = await compressImage(files[i], 800, 0.5);

        const docRef = await addDoc(collection(db, 'photos'), {
          name: photoName.trim(),
          thumb,
          createdAt: new Date().toISOString()
        });
        await setDoc(doc(db, 'photos_full', docRef.id), { image: full });
        uploaded += 1;
      } catch (error) {
        // fehlerhafte Datei überspringen, restliche weiter hochladen
      }
    }
    setPhotoUploading(false);
    setPhotoUploadProgress('');
    e.target.value = '';

    if (uploaded > 0) {
      setPhotoUploadDone(true);
      setTimeout(() => setPhotoUploadDone(false), 4000);
    } else {
      alert(t.photosUploadError);
    }
  };

  const openLightbox = async (index) => {
    setLightboxIndex(index);
    const photo = photos[index];
    if (photo && !fullImages[photo.id] && db) {
      try {
        const snap = await getDoc(doc(db, 'photos_full', photo.id));
        if (snap.exists()) {
          setFullImages(prev => ({ ...prev, [photo.id]: snap.data().image }));
        }
      } catch (error) {
        // Fallback: Thumbnail bleibt sichtbar
      }
    }
  };

  const handleDeletePhoto = async (id) => {
    if (!window.confirm('Foto wirklich löschen?')) return;
    await deleteDoc(doc(db, 'photos', id));
    try {
      await deleteDoc(doc(db, 'photos_full', id));
    } catch (error) {
      // Vollbild existiert ggf. nicht (Upload war unvollständig)
    }
  };

  // Vollbild eines Fotos laden (Cache → Firestore → Thumb als Fallback)
  const getFullImage = async (photo) => {
    if (fullImages[photo.id]) return fullImages[photo.id];
    try {
      const snap = await getDoc(doc(db, 'photos_full', photo.id));
      if (snap.exists()) {
        const image = snap.data().image;
        setFullImages(prev => ({ ...prev, [photo.id]: image }));
        return image;
      }
    } catch (error) {
      // Fallback unten
    }
    return photo.thumb;
  };

  const photoFileName = (photo, index) => {
    const stamp = photo.createdAt ? photo.createdAt.slice(0, 19).replace(/[:T]/g, '-') : '0000';
    const who = (photo.name || 'unbekannt').replace(/[^\wäöüÄÖÜß-]+/g, '_');
    return `${String(index + 1).padStart(3, '0')}_${stamp}_${who}_${photo.id.slice(0, 8)}.jpg`;
  };

  const triggerDownload = (href, filename) => {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadPhoto = async (photo, index) => {
    const dataUrl = await getFullImage(photo);
    triggerDownload(dataUrl, photoFileName(photo, index));
  };

  const handleDownloadAllPhotos = async () => {
    if (zipDownloading || photos.length === 0) return;
    setZipDownloading(true);
    try {
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      const sorted = [...photos].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      for (let i = 0; i < sorted.length; i++) {
        setZipProgress(`${i + 1} / ${sorted.length}`);
        const dataUrl = await getFullImage(sorted[i]);
        zip.file(photoFileName(sorted[i], i), dataUrl.split(',')[1], { base64: true });
      }
      setZipProgress('ZIP wird erstellt…');
      // JPEGs sind schon komprimiert → STORE (schneller, gleiche Größe)
      const blob = await zip.generateAsync({ type: 'blob', compression: 'STORE' });
      const url = URL.createObjectURL(blob);
      triggerDownload(url, 'hochzeitsfotos-galerie.zip');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      alert('Download fehlgeschlagen. Bitte erneut versuchen.');
    }
    setZipDownloading(false);
    setZipProgress('');
  };

  const handleDeleteGuest = async (id) => {
    if (window.confirm('Gast wirklich löschen?')) {
      await deleteDoc(doc(db, 'guests', id));
    }
  };

  const exportToCSV = () => {
    const headers = canViewPrivateGuestData
      ? ['Name', 'Vorname', 'Email', 'Status', 'Unterkunft', 'Gäste', 'Diät', 'Allergien', 'Nachricht', 'Datum']
      : ['Name', 'Vorname', 'Status', 'Gäste', 'Diät', 'Allergien', 'Datum'];
    const rows = guests.map((g) => (
      canViewPrivateGuestData
        ? [
            g.lastName || '',
            g.firstName || '',
            g.email || '',
            g.attending ? bt.accepted : bt.declined,
            g.attending
              ? g.hasAccommodation === true
                ? 'Unterkunft vorhanden'
                : g.hasAccommodation === false
                  ? 'Braucht Unterkunft'
                  : 'Noch nicht entschieden'
              : 'Nicht relevant',
            g.guests?.map((guest) => `${guest.name}${guest.age ? ` (${guest.age})` : ''}`).join('; ') || '',
            g.dietaryRestrictions || '',
            g.allergies || '',
            g.message || '',
            new Date(g.createdAt).toLocaleDateString(language === 'it' ? 'it-IT' : 'de-DE')
          ]
        : [
            g.lastName || '',
            g.firstName || '',
            g.attending ? bt.accepted : bt.declined,
            g.guests?.map((guest) => `${guest.name}${guest.age ? ` (${guest.age})` : ''}`).join('; ') || '',
            g.dietaryRestrictions || '',
            g.allergies || '',
            new Date(g.createdAt).toLocaleDateString(language === 'it' ? 'it-IT' : 'de-DE')
          ]
    ));

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `gaesteliste_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const addGuest = () => {
    setRsvpForm({ ...rsvpForm, guests: [...rsvpForm.guests, { name: '', age: '' }] });
  };

  const updateGuest = (index, field, value) => {
    const updated = [...rsvpForm.guests];
    updated[index] = { ...updated[index], [field]: value };
    setRsvpForm({ ...rsvpForm, guests: updated });
  };

  const removeGuest = (index) => {
    const updated = rsvpForm.guests.filter((_, i) => i !== index);
    setRsvpForm({ ...rsvpForm, guests: updated.length > 0 ? updated : [{ name: '', age: '' }] });
  };

  const handlePasswordSubmit = () => {
    const WEDDING_PASSWORD = process.env.REACT_APP_WEDDING_PASSWORD;
    
    if (WEDDING_PASSWORD && passwordInput === WEDDING_PASSWORD) {
      sessionStorage.setItem('wedding_access', 'granted');
      setPasswordError(false);
      setPasswordInput('');
      setIsAuthenticated(true);
    } else {
      setPasswordError(true);
      setTimeout(() => setPasswordError(false), 3000);
    }
  };


  const stats = {
    total: guests.reduce((sum, g) => {
      let count = 1; // Hauptgast
      if (g.guests && Array.isArray(g.guests)) {
        count += g.guests.filter(guest => guest.name && guest.name.trim() !== '').length;
      }
      return sum + count;
    }, 0),
    adults: guests.reduce((sum, g) => {
      let count = 1; // Hauptgast: standardmäßig als Erwachsener gezählt
      if (g.guests && Array.isArray(g.guests)) {
        // Erwachsene = Gäste mit Alter >= 11 oder ohne Altersangabe
        count += g.guests.filter(guest => 
          guest.name && 
          guest.name.trim() !== '' && 
          (!guest.age || guest.age === '' || parseInt(guest.age) >= 11)
        ).length;
      }
      return sum + count;
    }, 0),
    children: guests.reduce((sum, g) => {
      let count = 0;
      if (g.guests && Array.isArray(g.guests)) {
        // Kinder = 3 bis 10 Jahre
        count += g.guests.filter(guest => 
          guest.name && 
          guest.name.trim() !== '' && 
          guest.age && 
          guest.age !== '' && 
          parseInt(guest.age) >= 3 &&
          parseInt(guest.age) <= 10
        ).length;
      }
      return sum + count;
    }, 0),
    babies: guests.reduce((sum, g) => {
      let count = 0;
      if (g.guests && Array.isArray(g.guests)) {
        // Babys = 0 bis 2 Jahre
        count += g.guests.filter(guest => 
          guest.name && 
          guest.name.trim() !== '' && 
          guest.age && 
          guest.age !== '' && 
          parseInt(guest.age) >= 0 &&
          parseInt(guest.age) <= 2
        ).length;
      }
      return sum + count;
    }, 0),
    confirmed: guests.filter(g => g.attending).reduce((sum, g) => {
      let count = 1;
      if (g.guests && Array.isArray(g.guests)) {
        count += g.guests.filter(guest => guest.name && guest.name.trim() !== '').length;
      }
      return sum + count;
    }, 0),
    confirmedAdults: guests.filter(g => g.attending).reduce((sum, g) => {
      let count = 1; // Hauptgast als Erwachsener gezählt
      if (g.guests && Array.isArray(g.guests)) {
        count += g.guests.filter(guest => 
          guest.name && 
          guest.name.trim() !== '' && 
          (!guest.age || guest.age === '' || parseInt(guest.age) >= 11)
        ).length;
      }
      return sum + count;
    }, 0),
    confirmedChildren: guests.filter(g => g.attending).reduce((sum, g) => {
      let count = 0;
      if (g.guests && Array.isArray(g.guests)) {
        count += g.guests.filter(guest => 
          guest.name && 
          guest.name.trim() !== '' && 
          guest.age && 
          guest.age !== '' && 
          parseInt(guest.age) >= 3 &&
          parseInt(guest.age) <= 10
        ).length;
      }
      return sum + count;
    }, 0),
    confirmedBabies: guests.filter(g => g.attending).reduce((sum, g) => {
      let count = 0;
      if (g.guests && Array.isArray(g.guests)) {
        count += g.guests.filter(guest => 
          guest.name && 
          guest.name.trim() !== '' && 
          guest.age && 
          guest.age !== '' && 
          parseInt(guest.age) >= 0 &&
          parseInt(guest.age) <= 2
        ).length;
      }
      return sum + count;
    }, 0),
    declined: guests.filter(g => !g.attending).reduce((sum, g) => {
      let count = 1;
      if (g.guests && Array.isArray(g.guests)) {
        count += g.guests.filter(guest => guest.name && guest.name.trim() !== '').length;
      }
      return sum + count;
    }, 0)
  };

  // Backoffice Panel
  if (hasBackofficeAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-neutral-900 to-[#1a1a1a] text-gray-100">
        <div className="bg-black/80 border-b border-yellow-500/40 backdrop-blur-sm p-4 md:p-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {isVenueOnly ? (
                <Hotel className="w-6 h-6 md:w-8 md:h-8 text-amber-400" />
              ) : (
                <Crown className="w-6 h-6 md:w-8 md:h-8 text-yellow-500" />
              )}
              <div>
                <h1 className="text-xl md:text-3xl font-light tracking-widest uppercase">
                  {isVenueOnly ? bt.venuePanel : t.adminPanel}
                </h1>
                {isVenueOnly && (
                  <p className="text-xs md:text-sm text-gray-400 mt-1">{bt.venueGuestPrivacy}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 w-full md:w-auto">
              <div className="flex items-center gap-2 mr-2">
                {[
                  { code: 'de', label: 'DE' },
                  { code: 'it', label: 'IT' }
                ].map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => setLanguage(lang.code)}
                    className={`px-3 py-1 rounded-full text-xs tracking-widest border ${
                      language === lang.code
                        ? 'bg-white text-black border-white'
                        : 'bg-white/5 text-gray-300 border-white/20 hover:bg-white/10'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
              <button
                onClick={handleAdminLogout}
                className="bg-red-600/80 text-white px-3 md:px-4 py-2 rounded-full hover:bg-red-500 flex items-center gap-2 text-xs md:text-sm tracking-widest uppercase"
              >
                <X className="w-4 h-4" />
                <span className="hidden md:inline">{t.logout}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="sticky top-0 z-30 bg-neutral-950/80 border-b border-white/10 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex overflow-x-auto gap-1 py-2 scrollbar-hide">
              {backofficeTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setAdminView(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                    adminView === tab.id 
                      ? 'bg-amber-600 text-white' 
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-4 md:p-6">
          
          {/* GÄSTE VIEW */}
          {adminView === 'guests' && (
            <>
              {/* Filterleiste für Gäste */}
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="text-xs md:text-sm text-gray-400 uppercase tracking-[0.2em]">{bt.filter}</div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'all', label: bt.all },
                    { id: 'attending', label: bt.onlyConfirmed },
                    { id: 'declined', label: bt.onlyDeclined }
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setGuestFilter(opt.id)}
                      className={`px-3 py-1 rounded-full text-xs md:text-sm border transition-all ${
                        guestFilter === opt.id
                          ? 'bg-emerald-600 text-white border-emerald-400'
                          : 'bg-black/40 text-gray-300 border-white/10 hover:bg-black/60'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {(() => {
                let list = guests;
                if (guestFilter === 'attending') {
                  list = guests.filter(g => g.attending === true);
                } else if (guestFilter === 'declined') {
                  list = guests.filter(g => g.attending === false);
                }
                return (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-6 mb-6 md:mb-8">
                      <div className="bg-neutral-900/80 border border-white/5 p-3 md:p-6 rounded-xl md:rounded-2xl shadow-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.25em] uppercase text-gray-400">{t.totalGuests}</p>
                            <p className="text-2xl md:text-4xl font-light text-white">{stats.total}</p>
                            <p className="text-[9px] md:text-[11px] text-gray-500 mt-1 hidden md:block">👨‍👩‍👧‍👦 {bt.allGuestsNote}</p>
                          </div>
                          <Users className="w-8 h-8 md:w-12 md:h-12 text-blue-400" />
                        </div>
                      </div>
                      <div className="bg-neutral-900/80 border border-white/5 p-3 md:p-6 rounded-xl md:rounded-2xl shadow-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.25em] uppercase text-gray-400">{bt.adults}</p>
                            <p className="text-2xl md:text-4xl font-light text-purple-300">{stats.adults}</p>
                            <p className="text-[9px] md:text-[11px] text-gray-500 mt-1 hidden md:block">11+ Jahre</p>
                          </div>
                          <Users className="w-8 h-8 md:w-12 md:h-12 text-purple-300" />
                        </div>
                      </div>
                      <div className="bg-neutral-900/80 border border-white/5 p-3 md:p-6 rounded-xl md:rounded-2xl shadow-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.25em] uppercase text-gray-400">{bt.children}</p>
                            <p className="text-2xl md:text-4xl font-light text-orange-300">{stats.children}</p>
                            <p className="text-[9px] md:text-[11px] text-gray-500 mt-1 hidden md:block">3–10 Jahre</p>
                          </div>
                          <Users className="w-8 h-8 md:w-12 md:h-12 text-orange-300" />
                        </div>
                      </div>
                      <div className="bg-neutral-900/80 border border-white/5 p-3 md:p-6 rounded-xl md:rounded-2xl shadow-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.25em] uppercase text-gray-400">{bt.babies}</p>
                            <p className="text-2xl md:text-4xl font-light text-pink-300">{stats.babies}</p>
                            <p className="text-[9px] md:text-[11px] text-gray-500 mt-1 hidden md:block">0–2 Jahre</p>
                          </div>
                          <Users className="w-8 h-8 md:w-12 md:h-12 text-pink-300" />
                        </div>
                      </div>
                      <div className="bg-neutral-900/80 border border-white/5 p-3 md:p-6 rounded-xl md:rounded-2xl shadow-xl col-span-2 md:col-span-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.25em] uppercase text-gray-400">{t.declined}</p>
                            <p className="text-2xl md:text-4xl font-light text-red-400">{stats.declined}</p>
                            <p className="text-[9px] md:text-[11px] text-gray-500 mt-1 hidden md:block">✗ {bt.declinedNote}</p>
                          </div>
                          <X className="w-8 h-8 md:w-12 md:h-12 text-red-400" />
                        </div>
                      </div>
                    </div>

                    {/* Zusagen Details */}
                    <div className={`grid grid-cols-1 ${isVenueOnly ? 'md:grid-cols-3' : 'md:grid-cols-4'} gap-3 md:gap-6 mb-6 md:mb-8`}>
                      <div className="bg-gradient-to-br from-emerald-900/60 to-emerald-800/40 p-4 md:p-6 rounded-xl md:rounded-2xl shadow-xl border border-emerald-500/40">
                        <div className="text-center">
                          <Heart className="w-8 h-8 md:w-10 md:h-10 mx-auto mb-2 text-emerald-300" fill="currentColor" />
                          <p className="text-xs md:text-sm uppercase tracking-[0.15em] md:tracking-[0.25em] text-emerald-100/80">{t.confirmed} Gesamt</p>
                          <p className="text-3xl md:text-5xl font-light text-white my-2">{stats.confirmed}</p>
                          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-green-300">
                            <div className="flex justify-between text-xs md:text-sm mb-1 text-emerald-50">
                              <span>{bt.adults}:</span>
                              <span className="font-semibold">{stats.confirmedAdults}</span>
                            </div>
                            <div className="flex justify-between text-xs md:text-sm text-emerald-50">
                              <span>{bt.children}:</span>
                              <span className="font-semibold">{stats.confirmedChildren}</span>
                            </div>
                            <div className="flex justify-between text-xs md:text-sm mt-1 text-emerald-50">
                              <span>{bt.babies}:</span>
                              <span className="font-semibold">{stats.confirmedBabies}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-sky-900/60 to-sky-800/40 p-4 md:p-6 rounded-xl md:rounded-2xl shadow-xl border border-sky-500/40">
                        <div className="text-center">
                          <Users className="w-8 h-8 md:w-10 md:h-10 mx-auto mb-2 text-sky-300" />
                          <p className="text-xs md:text-sm uppercase tracking-[0.15em] md:tracking-[0.25em] text-sky-100/80">{bt.adults} {t.confirmed}</p>
                          <p className="text-3xl md:text-5xl font-light text-white my-2">{stats.confirmedAdults}</p>
                          <p className="text-[10px] md:text-xs text-sky-100/70 mt-3 md:mt-4">Menu & seating</p>
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-amber-900/60 to-amber-800/40 p-4 md:p-6 rounded-xl md:rounded-2xl shadow-xl border border-amber-500/40">
                        <div className="text-center">
                          <Users className="w-8 h-8 md:w-10 md:h-10 mx-auto mb-2 text-amber-300" />
                          <p className="text-xs md:text-sm uppercase tracking-[0.15em] md:tracking-[0.25em] text-amber-100/80">{bt.children} & {bt.babies}</p>
                          <p className="text-3xl md:text-5xl font-light text-white my-2">{stats.confirmedChildren + stats.confirmedBabies}</p>
                          <p className="text-[10px] md:text-xs text-amber-100/70 mt-3 md:mt-4">Kids menu & seating</p>
                        </div>
                      </div>
                      {!isVenueOnly && (
                        <div className="bg-gradient-to-br from-yellow-900/60 to-yellow-800/40 p-4 md:p-6 rounded-xl md:rounded-2xl shadow-xl border border-yellow-500/40">
                          <div className="text-center">
                            <span className="text-2xl md:text-3xl">💰</span>
                            <p className="text-xs md:text-sm uppercase tracking-[0.15em] md:tracking-[0.25em] text-yellow-100/80 mt-2">Gesamtkosten Gäste</p>
                            <p className="text-2xl md:text-4xl font-light text-white my-2">{(stats.confirmedBabies * PRICE_BABY + stats.confirmedChildren * PRICE_CHILD + stats.confirmedAdults * PRICE_ADULT).toLocaleString('de-DE')} €</p>
                            <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-yellow-500/50 space-y-1">
                              <div className="flex justify-between text-xs md:text-sm text-yellow-50">
                                <span>0–2 J.:</span>
                                <span className="font-semibold">{stats.confirmedBabies} × {PRICE_BABY} €</span>
                              </div>
                              <div className="flex justify-between text-xs md:text-sm text-yellow-50">
                                <span>3–10 J.:</span>
                                <span className="font-semibold">{stats.confirmedChildren} × {PRICE_CHILD} €</span>
                              </div>
                              <div className="flex justify-between text-xs md:text-sm text-yellow-50">
                                <span>11+ J.:</span>
                                <span className="font-semibold">{stats.confirmedAdults} × {PRICE_ADULT} €</span>
                              </div>
                              <div className="flex justify-between text-xs md:text-sm mt-2 pt-2 border-t border-yellow-500/30 text-yellow-50">
                                <span>Dienstleister:</span>
                                <span className="font-semibold">{vendors.length} × {PRICE_VENDOR} € = {(vendors.length * PRICE_VENDOR).toLocaleString('de-DE')} €</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Export Buttons */}
                    <div className="mb-4 md:mb-6">
                      <button
                        onClick={exportToCSV}
                        className="w-full md:w-auto bg-emerald-600/90 text-white px-4 md:px-6 py-3 rounded-full hover:bg-emerald-500 flex items-center justify-center gap-2 font-semibold tracking-widest text-xs uppercase shadow-lg"
                      >
                        <Download className="w-4 h-4 md:w-5 md:h-5" />
                        {t.exportCSV}
                      </button>
                      {isVenueOnly && (
                        <p className="text-xs text-gray-500 mt-2">{bt.csvVenueNotice}</p>
                      )}
                    </div>

                    {/* Guest List */}
                    <div className="bg-neutral-950/70 border border-white/10 rounded-xl md:rounded-2xl shadow-2xl overflow-hidden">
                      <div className="p-4 md:p-6 border-b border-white/10 flex items-center justify-between">
                        <h2 className="text-lg md:text-2xl font-light tracking-[0.15em] md:tracking-[0.25em] uppercase">{bt.guestOverviewTitle} ({list.length})</h2>
                        <span className="text-[10px] md:text-xs text-gray-400">RSVP</span>
                      </div>
                      
                      {/* Desktop Table */}
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-white/5">
                            <tr>
                              <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">Name</th>
                              {canViewPrivateGuestData && <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{bt.email}</th>}
                              <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">Status</th>
                              {canViewPrivateGuestData && <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{bt.accommodation}</th>}
                              <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{bt.guestCompanions}</th>
                              <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{bt.dietary}</th>
                              <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{bt.allergies}</th>
                              {canViewPrivateGuestData && <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{bt.message}</th>}
                              <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{bt.date}</th>
                              {canDeleteGuests && <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-300 uppercase tracking-widest">{bt.actions}</th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {list.map((guest) => (
                              <tr key={guest.id} className={guest.attending ? 'bg-emerald-900/10' : 'bg-red-900/10'}>
                                <td className="px-6 py-4">
                                  <div className="font-semibold text-gray-100">{guest.firstName} {guest.lastName}</div>
                                </td>
                                {canViewPrivateGuestData && <td className="px-6 py-4 text-xs text-gray-300">{guest.email}</td>}
                                <td className="px-6 py-4 text-xs">
                                  <span className={`px-2 py-1 rounded-full text-[11px] uppercase tracking-widest ${guest.attending ? 'bg-emerald-700/60 text-emerald-50' : 'bg-red-700/60 text-red-50'}`}>
                                    {guest.attending ? bt.accepted : bt.declined}
                                  </span>
                                </td>
                                {canViewPrivateGuestData && (
                                  <td className="px-6 py-4 text-xs text-gray-200">
                                    {guest.attending
                                      ? guest.hasAccommodation === true
                                        ? 'Unterkunft vorhanden'
                                        : guest.hasAccommodation === false
                                          ? 'Braucht Unterkunft'
                                          : 'Noch nicht entschieden'
                                      : 'Nicht relevant'}
                                  </td>
                                )}
                                <td className="px-6 py-4 text-xs text-gray-200">
                                  {guest.guests?.map((g, idx) => (
                                    <div key={idx}>{g.name}{g.age ? ` (${g.age})` : ''}</div>
                                  ))}
                                </td>
                                <td className="px-6 py-4 text-xs text-gray-300">{guest.dietaryRestrictions || '-'}</td>
                                <td className="px-6 py-4 text-xs text-gray-300">{guest.allergies || '-'}</td>
                                {canViewPrivateGuestData && <td className="px-6 py-4 text-xs text-gray-300 whitespace-pre-line max-w-[320px]">{guest.message || '-'}</td>}
                                <td className="px-6 py-4 text-xs text-gray-400">
                                  {new Date(guest.createdAt).toLocaleDateString(language === 'it' ? 'it-IT' : 'de-DE')}
                                </td>
                                {canDeleteGuests && (
                                  <td className="px-6 py-4">
                                    <button
                                      onClick={() => handleDeleteGuest(guest.id)}
                                      className="text-red-400 hover:text-red-200"
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View */}
                      <div className="md:hidden divide-y divide-white/10">
                        {list.map((guest) => (
                          <div key={guest.id} className={`p-4 ${guest.attending ? 'bg-emerald-900/10' : 'bg-red-900/10'}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <div className="font-semibold text-gray-100">{guest.firstName} {guest.lastName}</div>
                                {canViewPrivateGuestData && (
                                  <div className="text-xs text-gray-400 mt-1">{guest.email}</div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-wider ${guest.attending ? 'bg-emerald-700/60 text-emerald-50' : 'bg-red-700/60 text-red-50'}`}>
                                  {guest.attending ? '✓' : '✗'}
                                </span>
                                {canDeleteGuests && (
                                  <button
                                    onClick={() => handleDeleteGuest(guest.id)}
                                    className="text-red-400 hover:text-red-200 p-1"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                            {guest.attending && (
                              <div className="space-y-1 text-xs">
                                {canViewPrivateGuestData && (
                                  <div className="flex justify-between text-gray-300">
                                    <span>{bt.accommodation}:</span>
                                    <span className="text-gray-100">
                                      {guest.hasAccommodation === true ? 'Vorhanden' : guest.hasAccommodation === false ? 'Benötigt' : '–'}
                                    </span>
                                  </div>
                                )}
                                {guest.guests?.filter(g => g.name).length > 0 && (
                                  <div className="flex justify-between text-gray-300">
                                    <span>{bt.guestCompanions}:</span>
                                    <span className="text-gray-100">{guest.guests.filter(g => g.name).map(g => g.name).join(', ')}</span>
                                  </div>
                                )}
                                {guest.dietaryRestrictions && (
                                  <div className="flex justify-between text-gray-300">
                                    <span>{bt.dietary}:</span>
                                    <span className="text-gray-100">{guest.dietaryRestrictions}</span>
                                  </div>
                                )}
                                {guest.allergies && (
                                  <div className="flex justify-between text-gray-300">
                                    <span>{bt.allergies}:</span>
                                    <span className="text-gray-100">{guest.allergies}</span>
                                  </div>
                                )}
                                {canViewPrivateGuestData && guest.message && (
                                  <div className="flex flex-col text-gray-300">
                                    <span className="text-gray-300">{bt.message}:</span>
                                    <span className="text-gray-100 whitespace-pre-line">{guest.message}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="text-[10px] text-gray-500 mt-2">
                              {new Date(guest.createdAt).toLocaleDateString(language === 'it' ? 'it-IT' : 'de-DE')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
            </>
          )}

          {/* GÄSTEBUCH VERWALTUNG */}
          {adminView === 'guestbook' && (
            <div className="space-y-6">
              <div className="bg-neutral-950/70 border border-white/10 rounded-2xl shadow-2xl p-6">
                <h2 className="text-2xl font-light tracking-widest uppercase mb-6 text-gray-100">📖 Gästebuch-Verwaltung</h2>
                <p className="text-sm text-gray-400 mb-4">Hier kannst du Einträge aus dem öffentlichen Gästebuch einsehen und bei Bedarf löschen.</p>

                <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                  {guestbookMessages.map((entry) => (
                    <div key={entry.id} className="bg-black/40 border border-white/10 rounded-xl p-4 flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-semibold text-white">{entry.name || 'Unbekannt'}</div>
                          <div className="text-[11px] text-gray-400">
                            {entry.createdAt ? new Date(entry.createdAt).toLocaleString('de-DE') : ''}
                          </div>
                        </div>
                        <button
                          onClick={() => deleteDoc(doc(db, 'guestbook', entry.id))}
                          className="text-red-400 hover:text-red-300 text-xs px-2 py-1 border border-red-900/60 rounded"
                        >
                          Eintrag löschen
                        </button>
                      </div>
                      <div className="text-sm text-gray-100 whitespace-pre-line">
                        {entry.message}
                      </div>
                    </div>
                  ))}

                  {guestbookMessages.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <div className="text-4xl mb-3">📖</div>
                      <p>Noch keine Gästebuch-Einträge vorhanden.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* FOTO-VERWALTUNG */}
          {adminView === 'photos' && (
            <div className="space-y-6">
              <div className="bg-neutral-950/70 border border-white/10 rounded-2xl shadow-2xl p-6">
                <h2 className="text-2xl font-light tracking-widest uppercase mb-6 text-gray-100">📸 Foto-Verwaltung</h2>
                <p className="text-sm text-gray-400 mb-4">Von Gästen hochgeladene Fotos ansehen, herunterladen und bei Bedarf löschen. ({photos.length} Fotos)</p>

                <button
                  onClick={handleDownloadAllPhotos}
                  disabled={zipDownloading || photos.length === 0}
                  className="mb-6 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-black px-4 py-2 rounded-full text-sm font-medium uppercase tracking-widest disabled:opacity-50 disabled:cursor-wait"
                >
                  <Download className="w-4 h-4" />
                  {zipDownloading ? `Lädt… ${zipProgress}` : `Alle ${photos.length} Fotos als ZIP herunterladen`}
                </button>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[520px] overflow-y-auto pr-1">
                  {photos.map((photo, index) => (
                    <div key={photo.id} className="bg-black/40 border border-white/10 rounded-xl overflow-hidden flex flex-col">
                      <img
                        src={photo.thumb}
                        alt={`Foto von ${photo.name}`}
                        loading="lazy"
                        className="w-full aspect-square object-cover"
                      />
                      <div className="p-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-white truncate">{photo.name || 'Unbekannt'}</div>
                          <div className="text-[10px] text-gray-400">
                            {photo.createdAt ? new Date(photo.createdAt).toLocaleString('de-DE') : ''}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleDownloadPhoto(photo, index)}
                            className="text-amber-400 hover:text-amber-300 p-1 border border-amber-900/60 rounded"
                            title="Foto herunterladen"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeletePhoto(photo.id)}
                            className="text-red-400 hover:text-red-300 p-1 border border-red-900/60 rounded"
                            title="Foto löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {photos.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-4xl mb-3">📸</div>
                    <p>Noch keine Fotos hochgeladen.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* DIENSTLEISTER VIEW */}
          {adminView === 'vendors' && (
          <div className="space-y-6">
            <div className="bg-neutral-950/70 border border-white/10 rounded-2xl shadow-2xl p-6">
              <h2 className="text-2xl font-light tracking-widest uppercase mb-6 text-gray-100">🏪 Dienstleister-Verwaltung</h2>
              
              {/* Neuen Dienstleister hinzufügen */}
              <div className="bg-black/40 rounded-xl p-4 mb-6 border border-white/10">
                <h3 className="text-lg mb-4 text-amber-400">Neuen Dienstleister hinzufügen</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Name (z.B. Fotograf Müller)"
                    value={newVendor.name}
                    onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                    className="border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-amber-500 focus:outline-none"
                  />
                  <select
                    value={newVendor.category}
                    onChange={(e) => setNewVendor({ ...newVendor, category: e.target.value })}
                    className="border border-white/20 bg-black/40 text-gray-100 p-2 rounded focus:border-amber-500 focus:outline-none"
                  >
                    <option value="location">📍 Location</option>
                    <option value="catering">🍽️ Catering</option>
                    <option value="fotograf">📸 Fotograf</option>
                    <option value="videograf">🎬 Videograf</option>
                    <option value="musik">🎵 Musik/DJ</option>
                    <option value="florist">💐 Florist</option>
                    <option value="torte">🎂 Torte/Konditor</option>
                    <option value="brautkleid">👰 Brautkleid</option>
                    <option value="anzug">🤵 Anzug</option>
                    <option value="friseur">💇 Friseur/Styling</option>
                    <option value="deko">✨ Dekoration</option>
                    <option value="transport">🚗 Transport</option>
                    <option value="einladungen">💌 Einladungen/Papeterie</option>
                    <option value="sonstiges">📦 Sonstiges</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Kontaktperson"
                    value={newVendor.contact}
                    onChange={(e) => setNewVendor({ ...newVendor, contact: e.target.value })}
                    className="border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-amber-500 focus:outline-none"
                  />
                  <input
                    type="tel"
                    placeholder="Telefon"
                    value={newVendor.phone}
                    onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                    className="border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-amber-500 focus:outline-none"
                  />
                  <input
                    type="email"
                    placeholder="E-Mail"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                    className="border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-amber-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Angebot (z.B. 1.500€)"
                    value={newVendor.offer}
                    onChange={(e) => setNewVendor({ ...newVendor, offer: e.target.value })}
                    className="border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-amber-500 focus:outline-none"
                  />
                  <select
                    value={newVendor.status}
                    onChange={(e) => setNewVendor({ ...newVendor, status: e.target.value })}
                    className="border border-white/20 bg-black/40 text-gray-100 p-2 rounded focus:border-amber-500 focus:outline-none"
                  >
                    <option value="anfrage">📤 Anfrage gesendet</option>
                    <option value="angebot">📋 Angebot erhalten</option>
                    <option value="verhandlung">💬 In Verhandlung</option>
                    <option value="gebucht">✅ Gebucht</option>
                    <option value="abgesagt">❌ Abgesagt</option>
                  </select>
                  <textarea
                    placeholder="Notizen..."
                    value={newVendor.notes}
                    onChange={(e) => setNewVendor({ ...newVendor, notes: e.target.value })}
                    className="border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-amber-500 focus:outline-none md:col-span-2"
                    rows={2}
                  />
                  <button
                    onClick={async () => {
                      if (newVendor.name) {
                        await addDoc(collection(db, 'wedding_vendors'), {
                          ...newVendor,
                          createdAt: new Date().toISOString()
                        });
                        setNewVendor({ name: '', category: 'location', contact: '', phone: '', email: '', offer: '', status: 'anfrage', notes: '', rating: 0 });
                      }
                    }}
                    className="bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-500 transition-colors"
                  >
                    + Hinzufügen
                  </button>
                </div>
              </div>

              {/* Dienstleister-Liste nach Kategorie */}
              {['location', 'catering', 'fotograf', 'videograf', 'musik', 'florist', 'torte', 'brautkleid', 'anzug', 'friseur', 'deko', 'transport', 'einladungen', 'sonstiges'].map(cat => {
                const catVendors = vendors.filter(v => v.category === cat);
                if (catVendors.length === 0) return null;
                const catLabels = {
                  location: '📍 Location', catering: '🍽️ Catering', fotograf: '📸 Fotograf', videograf: '🎬 Videograf',
                  musik: '🎵 Musik/DJ', florist: '💐 Florist', torte: '🎂 Torte', brautkleid: '👰 Brautkleid',
                  anzug: '🤵 Anzug', friseur: '💇 Friseur', deko: '✨ Deko', transport: '🚗 Transport',
                  einladungen: '💌 Papeterie', sonstiges: '📦 Sonstiges'
                };
                return (
                  <div key={cat} className="mb-6">
                    <h3 className="text-lg text-amber-400 mb-3 border-b border-amber-400/30 pb-2">{catLabels[cat]}</h3>
                    <div className="space-y-3">
                      {catVendors.map(vendor => (
                        <div key={vendor.id} className="bg-black/30 rounded-lg p-4 border border-white/10 hover:border-amber-500/50 transition-colors">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="flex-1 min-w-[200px]">
                              <div className="font-semibold text-white text-lg">{vendor.name}</div>
                              {vendor.contact && <div className="text-sm text-gray-400">👤 {vendor.contact}</div>}
                              <div className="flex flex-wrap gap-3 mt-2 text-sm">
                                {vendor.phone && <a href={`tel:${vendor.phone}`} className="text-blue-400 hover:underline">📞 {vendor.phone}</a>}
                                {vendor.email && <a href={`mailto:${vendor.email}`} className="text-blue-400 hover:underline">✉️ {vendor.email}</a>}
                              </div>
                            </div>
                            <div className="text-right">
                              {vendor.offer && <div className="text-xl font-bold text-emerald-400">{vendor.offer}</div>}
                              <span className={`inline-block px-3 py-1 rounded-full text-xs mt-2 ${
                                vendor.status === 'gebucht' ? 'bg-emerald-600 text-white' :
                                vendor.status === 'verhandlung' ? 'bg-yellow-600 text-white' :
                                vendor.status === 'angebot' ? 'bg-blue-600 text-white' :
                                vendor.status === 'abgesagt' ? 'bg-red-600 text-white' :
                                'bg-gray-600 text-white'
                              }`}>
                                {vendor.status === 'anfrage' && '📤 Anfrage'}
                                {vendor.status === 'angebot' && '📋 Angebot'}
                                {vendor.status === 'verhandlung' && '💬 Verhandlung'}
                                {vendor.status === 'gebucht' && '✅ Gebucht'}
                                {vendor.status === 'abgesagt' && '❌ Abgesagt'}
                              </span>
                            </div>
                          </div>
                          {vendor.notes && <div className="mt-3 text-sm text-gray-400 bg-black/20 p-2 rounded">💭 {vendor.notes}</div>}
                          <div className="mt-3 flex gap-2">
                            <select
                              value={vendor.status}
                              onChange={async (e) => {
                                await updateDoc(doc(db, 'wedding_vendors', vendor.id), { status: e.target.value });
                              }}
                              className="text-xs bg-black/40 border border-white/20 text-gray-300 rounded px-2 py-1"
                            >
                              <option value="anfrage">📤 Anfrage</option>
                              <option value="angebot">📋 Angebot</option>
                              <option value="verhandlung">💬 Verhandlung</option>
                              <option value="gebucht">✅ Gebucht</option>
                              <option value="abgesagt">❌ Abgesagt</option>
                            </select>
                            <button
                              onClick={() => deleteDoc(doc(db, 'wedding_vendors', vendor.id))}
                              className="text-red-400 hover:text-red-300 text-xs px-2"
                            >
                              🗑️ Löschen
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {vendors.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">🏪</div>
                  <p>Noch keine Dienstleister hinzugefügt.</p>
                  <p className="text-sm mt-2">Füge oben deinen ersten Dienstleister hinzu!</p>
                </div>
              )}

              {/* Zusammenfassung */}
              {vendors.length > 0 && (
                <div className="mt-8 p-4 bg-gradient-to-r from-amber-900/30 to-amber-800/20 rounded-xl border border-amber-500/30">
                  <h3 className="text-lg text-amber-400 mb-3">📊 Übersicht</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-white">{vendors.length}</div>
                      <div className="text-xs text-gray-400">Gesamt</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-400">{vendors.filter(v => v.status === 'gebucht').length}</div>
                      <div className="text-xs text-gray-400">Gebucht</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-400">{vendors.filter(v => v.status === 'verhandlung').length}</div>
                      <div className="text-xs text-gray-400">In Verhandlung</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-400">{vendors.filter(v => v.status === 'angebot').length}</div>
                      <div className="text-xs text-gray-400">Angebot erhalten</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-400">{vendors.filter(v => v.status === 'anfrage').length}</div>
                      <div className="text-xs text-gray-400">Anfrage offen</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* CHECKLIST VIEW */}
          {adminView === 'checklist' && (
          <div className="space-y-6">
            <div className="bg-neutral-950/70 border border-white/10 rounded-2xl shadow-2xl p-6">
              <h2 className="text-2xl font-light tracking-widest uppercase mb-6 text-gray-100">📋 Hochzeits-Checklist</h2>
              
              {/* Quick Add Presets */}
              <div className="mb-6">
                <p className="text-sm text-gray-400 mb-3">Schnell hinzufügen aus Vorlagen:</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { months: 12, tasks: ['Budget festlegen', 'Datum festlegen', 'Gästeliste erstellen', 'Location suchen', 'Trauzeugen fragen'] },
                    { months: 10, tasks: ['Fotograf buchen', 'Videograf buchen', 'Catering auswählen', 'Save-the-Date versenden'] },
                    { months: 8, tasks: ['Brautkleid suchen', 'Anzug aussuchen', 'Florist auswählen', 'DJ/Band buchen'] },
                    { months: 6, tasks: ['Einladungen gestalten', 'Eheringe aussuchen', 'Hochzeitstorte bestellen', 'Friseur & Make-up buchen'] },
                    { months: 4, tasks: ['Einladungen versenden', 'Menü festlegen', 'Dekoration planen', 'Sitzordnung überlegen'] },
                    { months: 2, tasks: ['RSVP Deadline', 'Sitzplan finalisieren', 'Ablaufplan erstellen', 'Hochzeitsrede schreiben'] },
                    { months: 1, tasks: ['Letzte Anprobe', 'Dienstleister bestätigen', 'Trinkgelder vorbereiten', 'Notfall-Kit packen'] },
                    { months: 0, tasks: ['Ringe bereitlegen', 'Dokumente prüfen', 'Entspannen! 💕'] }
                  ].map(preset => (
                    <button
                      key={preset.months}
                      onClick={async () => {
                        for (const task of preset.tasks) {
                          const exists = checklistItems.some(item => item.task === task && item.monthsBefore === preset.months);
                          if (!exists) {
                            await addDoc(collection(db, 'wedding_checklist'), {
                              task,
                              monthsBefore: preset.months,
                              done: false,
                              createdAt: new Date().toISOString()
                            });
                          }
                        }
                      }}
                      className="px-3 py-1 bg-amber-600/30 text-amber-300 rounded-full text-xs hover:bg-amber-600/50 transition-colors"
                    >
                      {preset.months === 0 ? '🎊 Hochzeitstag' : `${preset.months} Monate vorher`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Timeline View */}
              {[12, 10, 8, 6, 4, 2, 1, 0].map(months => {
                const items = checklistItems.filter(item => item.monthsBefore === months);
                if (items.length === 0) return null;
                const allDone = items.every(item => item.done);
                return (
                  <div key={months} className={`mb-6 p-4 rounded-xl border ${allDone ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-black/30 border-white/10'}`}>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${allDone ? 'bg-emerald-600' : 'bg-amber-600'}`}>
                        {allDone ? '✓' : months}
                      </span>
                      <span className={allDone ? 'text-emerald-300' : 'text-amber-300'}>
                        {months === 0 ? '🎊 Hochzeitstag' : `${months} ${months === 1 ? 'Monat' : 'Monate'} vorher`}
                      </span>
                      <span className="text-xs text-gray-500 ml-auto">{items.filter(i => i.done).length}/{items.length} erledigt</span>
                    </h3>
                    <div className="space-y-2">
                      {items.map(item => (
                        <div key={item.id} className={`flex items-center gap-3 p-2 rounded-lg ${item.done ? 'bg-emerald-900/30' : 'bg-black/20'}`}>
                          <button
                            onClick={() => updateDoc(doc(db, 'wedding_checklist', item.id), { done: !item.done })}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              item.done ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-500 hover:border-amber-500'
                            }`}
                          >
                            {item.done && '✓'}
                          </button>
                          <span className={`flex-1 ${item.done ? 'line-through text-gray-500' : 'text-white'}`}>{item.task}</span>
                          <button
                            onClick={() => deleteDoc(doc(db, 'wedding_checklist', item.id))}
                            className="text-red-400 hover:text-red-300 text-xs opacity-50 hover:opacity-100"
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {checklistItems.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">📋</div>
                  <p>Noch keine Checklist-Einträge.</p>
                  <p className="text-sm mt-2">Klicke oben auf einen Zeitraum um Vorlagen hinzuzufügen!</p>
                </div>
              )}

              {/* Progress Overview */}
              {checklistItems.length > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-amber-900/30 to-emerald-900/30 rounded-xl border border-amber-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-amber-300">Gesamtfortschritt</span>
                    <span className="text-white font-bold">{Math.round((checklistItems.filter(i => i.done).length / checklistItems.length) * 100)}%</span>
                  </div>
                  <div className="w-full bg-black/50 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-amber-500 to-emerald-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${(checklistItems.filter(i => i.done).length / checklistItems.length) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-400">
                    <span>{checklistItems.filter(i => i.done).length} erledigt</span>
                    <span>{checklistItems.filter(i => !i.done).length} offen</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* SEATING VIEW */}
          {adminView === 'seating' && (
          <div className="space-y-6">
            <div className="bg-neutral-950/70 border border-white/10 rounded-2xl shadow-2xl p-6">
              <h2 className="text-2xl font-light tracking-widest uppercase mb-6 text-gray-100">{bt.seatingTab}</h2>
              
              {/* Add New Table */}
              <div className="bg-black/40 rounded-xl p-4 mb-6 border border-white/10">
                <h3 className="text-lg mb-4 text-amber-400">{bt.addTable}</h3>
                <div className="flex flex-wrap gap-3">
                  <input
                    type="text"
                    placeholder={bt.tableNamePlaceholder}
                    value={newTable.name}
                    onChange={(e) => setNewTable({ ...newTable, name: e.target.value })}
                    className="flex-1 min-w-[200px] border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-amber-500 focus:outline-none"
                  />
                  <input
                    type="number"
                    placeholder={bt.seats}
                    value={newTable.seats}
                    onChange={(e) => setNewTable({ ...newTable, seats: parseInt(e.target.value) || 8 })}
                    className="w-24 border border-white/20 bg-black/40 text-gray-100 p-2 rounded focus:border-amber-500 focus:outline-none"
                    min={1}
                    max={20}
                  />
                  <button
                    onClick={async () => {
                      if (newTable.name) {
                        await addDoc(collection(db, 'wedding_tables'), {
                          name: newTable.name,
                          seats: newTable.seats,
                          guests: [],
                          order: tables.length,
                          width: 160,
                          height: 100,
                          posX: 10 + ((tables.length % 4) * 20),
                          posY: 10 + (Math.floor(tables.length / 4) * 20),
                          createdAt: new Date().toISOString()
                        });
                        setNewTable({ name: '', seats: 8 });
                      }
                    }}
                    className="bg-amber-600 text-white px-4 py-2 rounded hover:bg-amber-500 transition-colors"
                  >
                    {bt.createTable}
                  </button>
                </div>
              </div>
              <div className="mb-6">
                <h3 className="text-lg mb-3 text-amber-400">{bt.roomPlan}</h3>
                <div
                  className="relative w-full h-[480px] bg-black/60 rounded-xl border border-white/10 overflow-hidden"
                >
                  {tables.map((table) => {
                    const baseX = typeof table.posX === 'number' ? table.posX : 10 + ((table.order || 0) % 4) * 20;
                    const baseY = typeof table.posY === 'number' ? table.posY : 10 + Math.floor((table.order || 0) / 4) * 20;
                    const left = Math.min(95, Math.max(5, baseX));
                    const top = Math.min(95, Math.max(5, baseY));
                    const assignedGuests = table.guests || [];
                    const width = typeof table.width === 'number' ? table.width : 160;
                    const height = typeof table.height === 'number' ? table.height : 100;
                    return (
                      <div
                        key={table.id}
                        className="absolute bg-black/70 border border-amber-500/60 rounded-xl flex flex-col items-center justify-center text-xs text-gray-100 cursor-move shadow-lg"
                        style={{ left: `${left}%`, top: `${top}%`, width, height }}
                        onMouseDown={(e) => {
                          if (e.button !== 0) return;
                          startDragging(e, table, left, top);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          if (e.touches && e.touches.length > 0) {
                            const touch = e.touches[0];
                            startDragging(touch, table, left, top);
                          }
                        }}
                      >
                        <div className="font-semibold text-sm truncate max-w-[7rem]">{table.name}</div>
                        <div className="text-[11px] text-gray-300">
                          {assignedGuests.length}/{table.seats} {bt.spots}
                        </div>
                        <div className="mt-1 flex flex-wrap justify-center gap-1 px-2">
                          {assignedGuests.length === 0 && (
                            <span className="text-[10px] text-gray-400">{bt.noGuestsAssigned}</span>
                          )}
                          {assignedGuests.slice(0, 6).map((guestName, idx) => {
                            const initials = guestName
                              .split(' ')
                              .filter(part => part)
                              .map(part => part[0])
                              .join('')
                              .slice(0, 2)
                              .toUpperCase();
                            return (
                              <span
                                key={idx}
                                className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-100"
                                title={guestName}
                              >
                                {initials || '?'}
                              </span>
                            );
                          })}
                          {assignedGuests.length > 6 && (
                            <span className="text-[10px] text-gray-300">
                              +{assignedGuests.length - 6}
                            </span>
                          )}
                        </div>
                        <div
                          className="absolute right-1 bottom-1 w-6 h-6 bg-amber-500 rounded-sm cursor-se-resize flex items-center justify-center"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            startResizing(e, table);
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (e.touches && e.touches.length > 0) {
                              startResizing(e.touches[0], table);
                            }
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 3H3v18h18V3z"></path>
                            <path d="M21 13H11v8M3 13h10M13 3v10h8"></path>
                          </svg>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tables Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tables.map(table => {
                  const assignedGuests = table.guests || [];
                  const freeSeats = table.seats - assignedGuests.length;
                  return (
                    <div key={table.id} className="bg-black/30 rounded-xl p-4 border border-white/10 hover:border-amber-500/50 transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-white">{table.name}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs ${freeSeats > 0 ? 'bg-emerald-600/50 text-emerald-200' : 'bg-red-600/50 text-red-200'}`}>
                          {freeSeats > 0 ? `${freeSeats} ${bt.free}` : bt.full}
                        </span>
                      </div>
                      
                      {/* Visual Table */}
                      <div className="relative w-full aspect-square max-w-[200px] mx-auto mb-4">
                        <div className="absolute inset-[25%] bg-amber-900/30 rounded-lg border-2 border-amber-600/50"></div>
                        {Array.from({ length: table.seats }).map((_, idx) => {
                          const seatsCount = table.seats;
                          const perSide = Math.max(1, Math.ceil(seatsCount / 4));
                          const sideIndex = Math.floor(idx / perSide);
                          const positionInSide = idx % perSide;
                          const ratio = perSide === 1 ? 0.5 : positionInSide / (perSide - 1);
                          let x = 50;
                          let y = 50;

                          if (sideIndex === 0) {
                            // Obere Kante
                            x = 25 + ratio * 50;
                            y = 15;
                          } else if (sideIndex === 1) {
                            // Rechte Kante
                            x = 85;
                            y = 25 + ratio * 50;
                          } else if (sideIndex === 2) {
                            // Untere Kante
                            x = 75 - ratio * 50;
                            y = 85;
                          } else {
                            // Linke Kante
                            x = 15;
                            y = 75 - ratio * 50;
                          }

                          const guest = assignedGuests[idx];
                          return (
                            <div
                              key={idx}
                              className={`absolute w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs transform -translate-x-1/2 -translate-y-1/2 ${
                                guest ? 'bg-amber-600 border-amber-400 text-white' : 'bg-gray-800 border-gray-600 text-gray-500'
                              }`}
                              style={{ left: `${x}%`, top: `${y}%` }}
                              title={guest || `${bt.seats} ${idx + 1}`}
                            >
                              {guest ? guest.charAt(0) : idx + 1}
                            </div>
                          );
                        })}
                      </div>

                      {/* Assigned Guests List */}
                      <div
                        className="space-y-1 mb-3"
                        onDragOver={(e) => {
                          e.preventDefault();
                        }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          const guestName = e.dataTransfer.getData('guest-name');
                          const sourceTableId = e.dataTransfer.getData('source-table-id');
                          if (!guestName || !sourceTableId || sourceTableId === table.id) return;
                          const sourceTable = tables.find(t => t.id === sourceTableId);
                          const targetTable = tables.find(t => t.id === table.id);
                          if (!sourceTable || !targetTable) return;
                          const sourceGuests = (sourceTable.guests || []).filter(name => name !== guestName);
                          const targetGuests = targetTable.guests || [];
                          if (targetGuests.includes(guestName)) return;
                          if (targetGuests.length >= (targetTable.seats || 0)) {
                            alert(bt.tableFull);
                            return;
                          }
                          const newTargetGuests = [...targetGuests, guestName];
                          try {
                            await updateDoc(doc(db, 'wedding_tables', sourceTableId), { guests: sourceGuests });
                            await updateDoc(doc(db, 'wedding_tables', table.id), { guests: newTargetGuests });
                          } catch (error) {
                            console.error(bt.moveGuestError, error);
                          }
                        }}
                      >
                        {assignedGuests.map((guest, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-sm bg-black/20 rounded px-2 py-1"
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.setData('guest-name', guest);
                              e.dataTransfer.setData('source-table-id', table.id);
                            }}
                          >
                            <span className="text-gray-300">{guest}</span>
                            <button
                              onClick={async () => {
                                const newGuests = [...assignedGuests];
                                newGuests.splice(idx, 1);
                                await updateDoc(doc(db, 'wedding_tables', table.id), { guests: newGuests });
                              }}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Add Guest to Table */}
                      {freeSeats > 0 && (
                        <select
                          className="w-full text-xs bg-black/40 border border-white/20 text-gray-300 rounded p-2"
                          value=""
                          onChange={async (e) => {
                            if (e.target.value) {
                              const newGuests = [...assignedGuests, e.target.value];
                              await updateDoc(doc(db, 'wedding_tables', table.id), { guests: newGuests });
                            }
                          }}
                        >
                          <option value="">{bt.addGuestToTable}</option>
                          {guests
                            .filter(g => g.attending)
                            .flatMap(g => {
                              const names = [`${g.firstName} ${g.lastName}`];
                              if (g.guests) {
                                g.guests.filter(gg => gg.name).forEach(gg => names.push(gg.name));
                              }
                              return names;
                            })
                            .filter(name => !tables.some(t => (t.guests || []).includes(name)))
                            .map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))
                          }
                        </select>
                      )}

                      {/* Delete Table */}
                      <button
                        onClick={() => deleteDoc(doc(db, 'wedding_tables', table.id))}
                        className="w-full mt-3 text-red-400 hover:text-red-300 text-xs py-1 border border-red-900/50 rounded hover:bg-red-900/20"
                      >
                        {bt.deleteTable}
                      </button>
                    </div>
                  );
                })}
              </div>

              {tables.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">🪑</div>
                  <p>{bt.noTablesYet}</p>
                  <p className="text-sm mt-2">{bt.createFirstTable}</p>
                </div>
              )}

              {/* Summary */}
              {tables.length > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-r from-amber-900/30 to-amber-800/20 rounded-xl border border-amber-500/30">
                  <h3 className="text-lg text-amber-400 mb-3">{bt.summary}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-white">{tables.length}</div>
                      <div className="text-xs text-gray-400">{bt.tables}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-400">{tables.reduce((sum, t) => sum + (t.guests || []).length, 0)}</div>
                      <div className="text-xs text-gray-400">{bt.seated}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-amber-400">{tables.reduce((sum, t) => sum + t.seats, 0)}</div>
                      <div className="text-xs text-gray-400">{bt.totalSeats}</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-400">
                        {guests.filter(g => g.attending).reduce((sum, g) => {
                          let count = 1;
                          if (g.guests) count += g.guests.filter(gg => gg.name).length;
                          return sum + count;
                        }, 0) - tables.reduce((sum, t) => sum + (t.guests || []).length, 0)}
                      </div>
                      <div className="text-xs text-gray-400">{bt.unseated}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* TODOS & BUDGET VIEW */}
          {(adminView === 'todos' || adminView === 'budget' || adminView === 'qrcode') && (
          <div className="grid grid-cols-1 gap-4 md:gap-8">
            {adminView === 'qrcode' && (
            <div className="bg-neutral-950/70 border border-white/10 rounded-2xl shadow-2xl p-6">
              <h2 className="text-xl font-light tracking-[0.25em] uppercase mb-4 text-gray-100">QR-Code für die Einladung</h2>
              <p className="text-sm text-gray-400 mb-6 max-w-xl">
                Dieser QR-Code führt Gäste direkt auf die Hochzeitsseite – ohne Passwort eingeben. Einfach auf die Einladung drucken oder als Bild speichern.
              </p>
              {process.env.REACT_APP_ACCESS_TOKEN ? (
                <>
                  <div className="flex flex-col md:flex-row items-start gap-6 md:gap-10">
                    <div className="bg-white p-4 rounded-xl inline-flex shrink-0">
                      <QRCodeCanvas
                        id="qr-invitation"
                        value={`${window.location.origin}/?token=${encodeURIComponent(process.env.REACT_APP_ACCESS_TOKEN || '')}`}
                        size={220}
                        level="H"
                        includeMargin={true}
                        imageSettings={{
                          src: logo,
                          height: 44,
                          width: 44,
                          excavate: true
                        }}
                      />
                    </div>
                    <div className="space-y-3 text-sm">
                      <p className="text-gray-300">Link für direkten Zugang (ohne Passwort):</p>
                      <code className="block bg-black/50 text-amber-200 px-3 py-2 rounded break-all text-xs">
                        {`${window.location.origin}/?token=***`}
                      </code>
                      <button
                        onClick={() => {
                          const canvas = document.getElementById('qr-invitation');
                          if (!canvas) return;
                          const png = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
                          const a = document.createElement('a');
                          a.href = png;
                          a.download = 'einladung-qrcode.png';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }}
                        className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-black px-4 py-2 rounded-full text-sm font-medium uppercase tracking-widest"
                      >
                        <Download className="w-4 h-4" />
                        QR-Code als PNG speichern
                      </button>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/10">
                    <h3 className="text-lg font-light tracking-[0.25em] uppercase mb-3 text-gray-100">📸 QR-Code für die Foto-Galerie</h3>
                    <p className="text-sm text-gray-400 mb-6 max-w-xl">
                      Zum Aufhängen in der Location: Gäste scannen den Code und landen direkt bei der Galerie, um ihre Fotos hochzuladen.
                    </p>
                    <div className="flex flex-col md:flex-row items-start gap-6 md:gap-10">
                      <div className="bg-white p-4 rounded-xl inline-flex shrink-0">
                        <QRCodeCanvas
                          id="qr-gallery"
                          value={`${window.location.origin}/?token=${encodeURIComponent(process.env.REACT_APP_ACCESS_TOKEN || '')}#photos`}
                          size={220}
                          level="H"
                          includeMargin={true}
                          imageSettings={{
                            src: logo,
                            height: 44,
                            width: 44,
                            excavate: true
                          }}
                        />
                      </div>
                      <div className="space-y-3 text-sm">
                        <p className="text-gray-300">Link direkt zur Galerie (ohne Passwort):</p>
                        <code className="block bg-black/50 text-amber-200 px-3 py-2 rounded break-all text-xs">
                          {`${window.location.origin}/?token=***#photos`}
                        </code>
                        <button
                          onClick={() => {
                            const canvas = document.getElementById('qr-gallery');
                            if (!canvas) return;
                            const png = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
                            const a = document.createElement('a');
                            a.href = png;
                            a.download = 'galerie-qrcode.png';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          }}
                          className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-black px-4 py-2 rounded-full text-sm font-medium uppercase tracking-widest"
                        >
                          <Download className="w-4 h-4" />
                          QR-Code als PNG speichern
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-amber-900/30 border border-amber-500/40 rounded-xl p-4 text-amber-200 text-sm">
                  <p>Für den QR-Code brauchst du in der <code className="bg-black/40 px-1 rounded">.env</code> die Variable <code className="bg-black/40 px-1 rounded">REACT_APP_ACCESS_TOKEN</code> (beliebiger langer Geheim-String). So steht das Passwort nicht in der URL – Gäste scannen den QR und kommen direkt rein.</p>
                </div>
              )}
            </div>
            )}
            {adminView === 'todos' && (
            <div className="bg-neutral-950/70 border border-white/10 rounded-2xl shadow-2xl p-6">
              <h2 className="text-xl font-light tracking-[0.25em] uppercase mb-4 text-gray-100">ToDos für die Hochzeitsplanung</h2>
              <div className="mb-4 grid grid-cols-1 gap-3">
                <select
                  className="w-full border border-white/20 bg-black/60 text-gray-100 text-sm rounded p-2 focus:border-yellow-500 focus:outline-none"
                  value=""
                  onChange={(e) => {
                    const preset = TODO_PRESETS[parseInt(e.target.value, 10)];
                    if (preset) {
                      setNewTodo(prev => ({
                        ...prev,
                        title: preset.title,
                        category: preset.category
                      }));
                    }
                  }}
                >
                  <option value="" className="bg-black">Aus Vorlage wählen …</option>
                  {TODO_PRESETS.map((preset, index) => (
                    <option key={index} value={index} className="bg-black">
                      {preset.title} ({preset.category})
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Titel (z.B. Videograf buchen)"
                  value={newTodo.title}
                  onChange={(e) => setNewTodo({ ...newTodo, title: e.target.value })}
                  className="w-full border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-yellow-500 focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="Kategorie (z.B. Dienstleister)"
                  value={newTodo.category}
                  onChange={(e) => setNewTodo({ ...newTodo, category: e.target.value })}
                  className="w-full border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-yellow-500 focus:outline-none"
                />
                <textarea
                  placeholder="Notiz (optional)"
                  value={newTodo.note}
                  onChange={(e) => setNewTodo({ ...newTodo, note: e.target.value })}
                  className="w-full border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-yellow-500 focus:outline-none h-20"
                />
                <button
                  onClick={addTodo}
                  className="bg-yellow-500/90 text-black px-4 py-2 rounded-full hover:bg-yellow-400 text-xs uppercase tracking-widest shadow-lg"
                >
                  ToDo hinzufügen
                </button>
              </div>

              <div className="space-y-2 max-h-80 overflow-y-auto">
                {adminTodos.map((todo) => (
                  <div
                    key={todo.id}
                    className="border border-white/10 rounded-xl p-3 flex items-start justify-between gap-3 bg-white/5"
                  >
                    <div>
                      <div className="font-semibold text-gray-100">{todo.title}</div>
                      {todo.category && (
                        <div className="text-xs text-gray-400 mb-1">{todo.category}</div>
                      )}
                      {todo.note && (
                        <div className="text-xs text-gray-300 whitespace-pre-line">{todo.note}</div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={() => toggleTodoStatus(todo)}
                        className={`px-3 py-1 rounded text-xs font-semibold uppercase tracking-widest ${
                          todo.status === 'open'
                            ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                            : todo.status === 'in_progress'
                              ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                              : 'bg-green-500/20 text-green-300 border border-green-500/40'
                        }`}
                      >
                        {todo.status === 'open' && 'Offen'}
                        {todo.status === 'in_progress' && 'In Arbeit'}
                        {todo.status === 'done' && 'Erledigt'}
                      </button>
                      <button
                        onClick={() => deleteTodo(todo.id)}
                        className="text-red-400 hover:text-red-200 text-xs"
                      >
                        Löschen
                      </button>
                    </div>
                  </div>
                ))}
                {adminTodos.length === 0 && (
                  <p className="text-sm text-gray-400">Noch keine ToDos angelegt.</p>
                )}
              </div>
            </div>
            )}

            {adminView === 'budget' && (
            <div className="bg-neutral-950/70 border border-white/10 rounded-2xl shadow-2xl p-6">
              <h2 className="text-xl font-light tracking-[0.25em] uppercase mb-4 text-gray-100">Budgetplanung</h2>
              <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <select
                  className="md:col-span-2 w-full border border-white/20 bg-black/60 text-gray-100 text-sm rounded p-2 focus:border-yellow-500 focus:outline-none"
                  value=""
                  onChange={(e) => {
                    const todoId = e.target.value;
                    const todo = adminTodos.find(t => t.id === todoId);
                    if (todo) {
                      setNewBudgetItem(prev => ({
                        ...prev,
                        title: todo.title || prev.title,
                        category: todo.category || prev.category
                      }));
                    }
                  }}
                >
                  <option value="" className="bg-black">ToDo als Budgetposition übernehmen …</option>
                  {adminTodos.map(todo => (
                    <option key={todo.id} value={todo.id} className="bg-black">
                      {todo.title}{todo.category ? ` (${todo.category})` : ''}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Position (z.B. Videograf)"
                  value={newBudgetItem.title}
                  onChange={(e) => setNewBudgetItem({ ...newBudgetItem, title: e.target.value })}
                  className="w-full border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-yellow-500 focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="Kategorie"
                  value={newBudgetItem.category}
                  onChange={(e) => setNewBudgetItem({ ...newBudgetItem, category: e.target.value })}
                  className="w-full border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-yellow-500 focus:outline-none"
                />
                <input
                  type="number"
                  placeholder="Geplant (€)"
                  value={newBudgetItem.planned}
                  onChange={(e) => setNewBudgetItem({ ...newBudgetItem, planned: e.target.value })}
                  className="w-full border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-yellow-500 focus:outline-none"
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Tatsächlich (€)"
                  value={newBudgetItem.actual}
                  onChange={(e) => setNewBudgetItem({ ...newBudgetItem, actual: e.target.value })}
                  className="w-full border border-white/20 bg-black/40 text-gray-100 placeholder-gray-500 p-2 rounded focus:border-yellow-500 focus:outline-none"
                  min="0"
                />
                <button
                  onClick={addBudgetItem}
                  className="md:col-span-2 bg-yellow-500/90 text-black px-4 py-2 rounded-full hover:bg-yellow-400 text-xs uppercase tracking-widest shadow-lg"
                >
                  Budgetposten hinzufügen
                </button>
              </div>

              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] uppercase tracking-widest text-gray-300">Position</th>
                      <th className="px-3 py-2 text-left text-[11px] uppercase tracking-widest text-gray-300">Kategorie</th>
                      <th className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-gray-300">Geplant</th>
                      <th className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-gray-300">Tatsächlich</th>
                      <th className="px-3 py-2 text-center text-[11px] uppercase tracking-widest text-gray-300">Status</th>
                      <th className="px-3 py-2 text-center text-[11px] uppercase tracking-widest text-gray-300">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {adminBudget.map((item) => (
                      <tr key={item.id} className="border-t border-white/5">
                        <td className="px-3 py-2 text-gray-100">{item.title}</td>
                        <td className="px-3 py-2 text-gray-400">{item.category}</td>
                        <td className="px-3 py-2 text-right text-gray-100">{(item.planned || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
                        <td className="px-3 py-2 text-right text-gray-100">{(item.actual || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => updateBudgetStatus(item, item.status === 'paid' ? 'open' : 'paid')}
                            className={`px-3 py-1 rounded text-xs font-semibold uppercase tracking-widest ${
                              item.status === 'paid'
                                ? 'bg-green-500/20 text-green-300 border border-green-500/40'
                                : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40'
                            }`}
                          >
                            {item.status === 'paid' ? 'Bezahlt' : 'Offen'}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => deleteBudgetItem(item.id)}
                            className="text-red-400 hover:text-red-200 text-xs"
                          >
                            Löschen
                          </button>
                        </td>
                      </tr>
                    ))}
                    {adminBudget.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-3 py-4 text-sm text-gray-400 text-center">
                          Noch keine Budgeteinträge angelegt.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="text-sm text-gray-100 space-y-1">
                <div>
                  Geplantes Gesamtbudget:{' '}
                  {(adminBudget.reduce((sum, i) => sum + (i.planned || 0), 0)).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </div>
                <div>
                  Bisher ausgegeben:{' '}
                  {(adminBudget.reduce((sum, i) => sum + (i.actual || 0), 0)).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </div>
                <div>
                  Differenz:{' '}
                  {(adminBudget.reduce((sum, i) => sum + (i.planned || 0), 0) - adminBudget.reduce((sum, i) => sum + (i.actual || 0), 0)).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                </div>
              </div>
            </div>
            )}
          </div>
          )}
        </div>
      </div>
    );
  }

  // Admin Login Modal
  if (showAdminLogin) {
    return (
      <div className="min-h-screen bg-gray-900/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <Lock className="w-16 h-16 mx-auto mb-4 text-gray-700" />
            <h2 className="text-2xl font-bold">{loginMode === 'venue' ? bt.venueLogin : t.adminLogin}</h2>
          </div>
          <div className="space-y-4">
            {!firebaseReady && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-3 animate-fadeIn">
                <p className="text-amber-800 text-sm">
                  {language === 'it'
                    ? 'Configurazione Firebase locale mancante. Aggiungi le variabili REACT_APP_FIREBASE_* in .env.local.'
                    : 'Lokale Firebase-Konfiguration fehlt. Bitte REACT_APP_FIREBASE_* in .env.local ergänzen.'}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold mb-2">Email</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full border-2 border-gray-300 p-3 rounded focus:border-black focus:outline-none mb-3"
              />
              <label className="block text-sm font-semibold mb-2">{t.password}</label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin('venue')}
                className="w-full border-2 border-gray-300 p-3 rounded focus:border-black focus:outline-none"
              />
              {adminError && <p className="text-red-500 text-sm mt-2">{adminError}</p>}
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleAdminLogin}
                className="flex-1 bg-black text-white py-3 rounded hover:bg-gray-800"
              >
                {t.login}
              </button>
              <button
                onClick={() => setShowAdminLogin(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 rounded hover:bg-gray-400"
              >
                {language === 'it' ? 'Chiudi' : 'Schließen'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasBackofficeAccess && isVenueLoginRoute) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4 bg-black">
        <div
          className="absolute inset-0 access-bg"
          style={{ backgroundImage: 'url(/trani-night.png)' }}
        ></div>
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10 bg-white/40 backdrop-blur-md p-8 md:p-12 rounded-2xl shadow-2xl max-w-md w-full animate-fadeIn">
          <div className="flex justify-end gap-2 mb-6">
            {['de', 'it'].map((lang) => (
              <button
                key={lang}
                onClick={() => setLanguage(lang)}
                className={`px-3 py-1 rounded-full text-xs tracking-widest border ${
                  language === lang
                    ? 'bg-white text-black border-white'
                    : 'bg-white/10 text-white border-white/30'
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="text-center mb-8">
            <Hotel className="w-14 h-14 mx-auto mb-4 text-amber-300" />
            <h2 className="text-3xl font-light tracking-[0.2em] uppercase text-gray-900">{bt.venueAccessTitle}</h2>
            <p className="text-sm text-gray-800 mt-4">{bt.venueAccessSubtitle}</p>
            <p className="text-xs text-gray-700 mt-3">{bt.venueAccessHint}</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Email</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="w-full border-2 border-gray-300 p-3 rounded focus:border-black focus:outline-none mb-3"
              />
              <label className="block text-sm font-semibold mb-2">{t.password}</label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
                className="w-full border-2 border-gray-300 p-3 rounded focus:border-black focus:outline-none"
              />
              {adminError && <p className="text-red-600 text-sm mt-2">{adminError}</p>}
            </div>
            <button
              onClick={() => handleAdminLogin('venue')}
              className="w-full bg-black text-white py-4 rounded-lg hover:bg-gray-800 transition-all uppercase tracking-widest text-sm"
            >
              {bt.venueLogin}
            </button>
            <a
              href="/"
              className="block text-center text-xs text-gray-800 underline underline-offset-4"
            >
              {language === 'it' ? 'Torna all’accesso ospiti' : 'Zurück zum Gäste-Zugang'}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Access Control Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4 bg-black">
        <div
          className="absolute inset-0 access-bg"
          style={{ backgroundImage: 'url(/trani-night.png)' }}
        ></div>
        <div className="absolute inset-0 bg-black/45"></div>

        <div className="relative z-10 bg-white/35 backdrop-blur-md p-8 md:p-12 rounded-2xl shadow-2xl max-w-md w-full animate-fadeIn">
          <div className="text-center mb-8">
            <Heart className="w-16 h-16 mx-auto mb-4 text-red-400 animate-pulse-slow" fill="currentColor" />
            <div
              className="text-3xl md:text-4xl gold-text px-4 py-2 overflow-visible"
              style={{
                fontFamily: "'Great Vibes', cursive",
                lineHeight: '1.8',
                textShadow: '0 2px 20px rgba(212, 175, 55, 0.3)'
              }}
            >
              <div>Joanna</div>
              <div className="leading-none">&</div>
              <div>Domenico</div>
            </div>
            <p className="text-gray-900 text-lg">11. August 2026</p>
            <div className="h-px w-24 bg-gray-300 mx-auto my-6"></div>
            <p className="text-sm text-gray-800">
              Diese Seite ist nur für eingeladene Gäste
            </p>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest mb-2 text-gray-800">
                Passwort
              </label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                placeholder="Passwort von der Einladung"
                className={`w-full border-2 ${
                  passwordError ? 'border-red-500 shake' : 'border-gray-300'
                } p-3 rounded-lg focus:border-black focus:outline-none transition-all text-gray-900 placeholder-gray-700`}
              />
            </div>
            
            {passwordError && (
              <div className="bg-red-50 border-l-4 border-red-500 p-3 animate-fadeIn">
                <p className="text-red-700 text-sm flex items-center gap-2">
                  <X className="w-4 h-4" />
                  Falsches Passwort. Bitte überprüfe deine Einladung.
                </p>
              </div>
            )}
            
            <button
              onClick={handlePasswordSubmit}
              className="w-full bg-black text-white py-4 rounded-lg hover:bg-gray-800 transition-all uppercase tracking-widest text-sm hover:shadow-lg transform hover:-translate-y-0.5"
            >
              Zugang erhalten
            </button>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-700 text-center leading-relaxed">
                Das Passwort findest du auf deiner Einladung<br />
                oder im QR-Code für direkten Zugang
              </p>
              <a
                href="/?venue=login"
                className="block mt-4 text-center text-xs uppercase tracking-[0.2em] text-gray-900 underline underline-offset-4"
              >
                {bt.venueAccessLink}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main Website continues...
  return (
    <div className="bg-white" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif" }}>
      <SpeedInsights />
      {/* Navigation */}
      <nav className={`fixed top-0 w-full bg-black/40 text-white shadow-sm z-40 border-b border-transparent transform transition-transform duration-300 ${showNavbar ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo2.png"
              alt="Joanna & Domenico Logo"
              className="h-10 w-10 object-contain animate-fadeDown"
            />
            <div className="text-2xl font-bold tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              J & D
            </div>
          </div>
          
          <div className="hidden md:flex gap-8 text-sm uppercase tracking-widest items-center">
            {[
              { label: t.home, ref: homeRef },
              { label: t.ourStory, ref: storyRef },
              { label: t.details, ref: detailsRef },
              { label: t.rsvp, ref: rsvpRef },
              { label: t.photos, ref: photosSectionRef },
              { label: t.location, ref: locationRef }
            ].map(item => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.ref)}
                className={`hover:text-gray-300 transition-all duration-300 ${activeSection === item.ref.current?.id ? 'border-b-2 border-white' : ''}`}
              >
                {item.label}
              </button>
            ))}
            <div className="flex gap-2 items-center ml-2">
              {[
                { code: 'de', flag: '🇩🇪', label: 'DE' },
                { code: 'en', flag: '🇬🇧', label: 'EN' },
                { code: 'it', flag: '🇮🇹', label: 'IT' },
                { code: 'pl', flag: '🇵🇱', label: 'PL' }
              ].map(lang => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`px-3 py-1.5 rounded-lg text-base transition-all duration-300 transform hover:scale-110 ${
                    language === lang.code 
                      ? 'bg-white/30 backdrop-blur-sm border-2 border-white shadow-lg' 
                      : 'bg-white/10 border-2 border-white/30 hover:bg-white/20'
                  }`}
                  title={lang.label}
                >
                  {lang.flag}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setLoginMode('admin');
                setShowAdminLogin(true);
              }}
              className="text-gray-300 hover:text-white transition-colors"
              title="Admin"
            >
              <Lock className="w-4 h-4" />
            </button>
          </div>

          <button 
            className="md:hidden z-50 relative"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Outside nav for correct z-index layering */}
      <div 
        className={`md:hidden fixed inset-0 bg-black/95 backdrop-blur-lg z-50 transition-all duration-500 flex items-center justify-center ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      >
        <div 
          className={`flex flex-col items-center transition-all duration-500 delay-100 ${
            mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-10 opacity-0'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Navigation Links */}
          <div className="space-y-8 mb-12">
            {[
              { label: t.home, ref: homeRef, icon: Heart },
              { label: t.ourStory, ref: storyRef, icon: Heart },
              { label: t.details, ref: detailsRef, icon: Crown },
              { label: t.rsvp, ref: rsvpRef, icon: MessageSquare },
              { label: t.photos, ref: photosSectionRef, icon: Camera },
              { label: t.location, ref: locationRef, icon: MapPin }
            ].map((item, index) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.ref)}
                className={`flex items-center justify-center gap-3 w-full text-center py-3 text-2xl uppercase tracking-[0.3em] text-white hover:text-gray-300 transition-all duration-300 transform hover:scale-110 ${
                  mobileMenuOpen ? 'animate-fadeIn' : ''
                }`}
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  fontFamily: "'Cormorant Garamond', serif"
                }}
              >
                <item.icon className="w-5 h-5 opacity-60" />
                {item.label}
              </button>
            ))}
          </div>

          {/* Language Selector */}
          <div className="mb-8">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-4 text-center">
              Sprache / Language
            </p>
            <div className="flex gap-3 justify-center">
              {[
                { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
                { code: 'en', flag: '🇬🇧', label: 'English' },
                { code: 'it', flag: '🇮🇹', label: 'Italiano' },
                { code: 'pl', flag: '🇵🇱', label: 'Polski' }
              ].map(lang => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setMobileMenuOpen(false);
                  }}
                  className={`p-4 rounded-xl text-3xl transition-all duration-300 transform hover:scale-110 ${
                    language === lang.code 
                      ? 'bg-white/30 border-2 border-white shadow-2xl scale-110' 
                      : 'bg-white/10 border-2 border-white/30 hover:bg-white/20'
                  }`}
                  title={lang.label}
                >
                  {lang.flag}
                </button>
              ))}
            </div>
          </div>

          {/* Admin Login Button */}
          <button
            onClick={() => {
              setLoginMode('admin');
              setShowAdminLogin(true);
              setMobileMenuOpen(false);
            }}
            className="bg-white/10 border-2 border-white/30 text-white px-8 py-4 rounded-full flex items-center gap-3 hover:bg-white/20 transition-all transform hover:scale-105"
          >
            <Lock className="w-5 h-5" />
            <span className="uppercase tracking-widest text-sm">{t.adminLogin}</span>
          </button>
        </div>
      </div>

      {/* Hero + Countdown Section - Ein durchgehendes Bild */}
      <section className="relative">
        {/* Durchgehendes Hintergrundbild */}
        <div className="absolute inset-0 bg-cover bg-right md:bg-center" style={{
          backgroundImage: 'url(/IMG-20251117-WA0005.webp)'
        }}></div>
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* Hero Content */}
        <div ref={homeRef} id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="relative z-10 text-center text-white px-4 animate-fadeIn">
            <div className="mb-8">
              <div className="flex flex-col items-center">
                <div
                  className="hero-name text-5xl md:text-6xl gold-text px-8 py-2 overflow-visible"
                  style={{
                    fontFamily: "'Great Vibes', cursive",
                    lineHeight: '1.2',
                    textShadow: '0 2px 20px rgba(212, 175, 55, 0.3)',
                    marginBottom: '0.25rem'
                  }}
                >
                  Joanna
                </div>
                <div 
                  className="text-4xl md:text-5xl gold-text -mt-1 mb-0.5"
                  style={{
                    fontFamily: "'Great Vibes', cursive",
                    textShadow: '0 2px 20px rgba(212, 175, 55, 0.3)'
                  }}
                >
                  &
                </div>
                <div
                  className="hero-name text-5xl md:text-6xl gold-text px-8 py-2 overflow-visible"
                  style={{
                    fontFamily: "'Great Vibes', cursive",
                    lineHeight: '1.2',
                    textShadow: '0 2px 20px rgba(212, 175, 55, 0.3)',
                    marginTop: '-0.25rem'
                  }}
                >
                  Domenico
                </div>
              </div>
            </div>
            
            <div className="elegant-divider w-32 mx-auto mb-8"></div>
            
            <div className="text-xl md:text-2xl tracking-[0.3em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              {t.areGettingMarried}
            </div>
            
            <div className="text-lg md:text-xl tracking-widest uppercase mb-12">
              11 • 08 • 2026
            </div>

            <button
              onClick={() => scrollToSection(rsvpRef)}
              className="btn-elegant bg-white text-black px-10 py-4 uppercase tracking-widest text-sm hover:bg-black hover:text-white transition-all duration-300 border-2 border-white hover:scale-105 transform rounded-sm"
            >
              RSVP
            </button>

            <div className="absolute bottom-10 inset-x-0 flex justify-center animate-bounce">
              <ChevronDown className="w-8 h-8" />
            </div>
          </div>
        </div>

        {/* Countdown - Direkt darunter, gleiches Bild */}
        <div className="relative py-20">
          <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
            <h2 className="text-4xl md:text-5xl mb-4 tracking-wider text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.untilBigDay}
            </h2>
            <div className="h-px w-24 bg-white mx-auto mb-12"></div>
            
            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-2xl mx-auto">
              {[
                { value: countdown.days, label: t.days },
                { value: countdown.hours, label: t.hours },
                { value: countdown.minutes, label: t.minutes }
              ].map((item, i) => (
                <div
                  key={i}
                  className="card-shine flex flex-col items-center justify-center bg-white/15 backdrop-blur-md px-4 py-6 md:p-8 rounded-xl border border-white/20 min-h-[120px] md:min-h-[160px] hover:bg-white/25 hover:border-white/40 transition-all duration-500 transform hover:scale-105 shadow-lg"
                >
                  <div className="text-5xl md:text-7xl font-light mb-2 text-white text-glow" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {String(item.value).padStart(2, '0')}
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/80">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </section>

      {/* Story - Full German text kept as is */}
      <section
        ref={storyRef}
        id="story"
        className={`py-20 love-story-section section-animate ${isVisible.story ? 'visible' : ''}`}
      >
        <div
          className="love-story-bg"
          id="loveStoryBg"
          style={{ backgroundImage: "url(/finish.webp)" }}
        />
        <div className="love-story-overlay"></div>
        <div className="max-w-4xl mx-auto px-6 love-story-content">
          <h2 className="text-4xl md:text-5xl text-center mb-4 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t.loveStoryTitle}
          </h2>
          <div className="h-px w-24 bg-black mx-auto mb-6"></div>

          <div className="space-y-6 text-lg leading-relaxed text-gray-700" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {language === 'it' ? (
              <>
                <p className="text-xl italic text-center mb-8">
                  A volte la vita scrive storie così vere che sono più belle di qualsiasi favola – la nostra è proprio così.
                </p>
                <p>Ci siamo incontrati per la prima volta a 14 anni, a scuola. Già allora lui mi notò e mi ammirava in silenzio, mentre io ero interessata a tutto – tranne che ai ragazzi. 😄</p>
                <p>Più tardi abbiamo frequentato la stessa scuola professionale di economia domestica. Andavamo d'accordo, ma tra noi non c'era più che amicizia. Passarono gli anni, finché una notte ci rincontrammo per caso in una discoteca dove lui lavorava. Ma il momento non era quello giusto – lui era impegnato e io ricevetti il mio primo piccolo "due di picche".</p>
                <p>Ogni tanto mi scriveva su Facebook con messaggi tipo "Come stai?", ma la storia si addormentò di nuovo.</p>
                <p>Solo alla fine del 2018 le nostre strade si incrociarono ancora – per caso, in un bar. Salutai un conoscente e all'inizio non lo vidi nemmeno, cosa che ovviamente lo ferì un po' 😉. Poi iniziammo a parlare, a ridere e decidemmo di uscire a ballare insieme. Due settimane dopo è successo – una notte spensierata, senza aspettative, piena di leggerezza.</p>
                <p>Poco dopo passammo di nuovo del tempo insieme, guardando film – e all'inizio del 2019 ormai era fatta: tra noi era nato qualcosa di vero e profondo.</p>
                <p className="font-semibold">Ma presto la vita ci mise alla prova per la prima volta.</p>
                <p>Un colpo del destino arrivò all'improvviso e scosse la nostra giovane relazione. Fu un periodo pieno di emozioni, dubbi e dolore – ma anche un tempo in cui abbiamo imparato cosa significa esserci davvero l'uno per l'altra. Siamo rimasti uniti, anche quando era difficile, e questo ci ha reso ancora più forti.</p>
                <p>Nel novembre 2020 siamo stati benedetti con nostra figlia – il nostro primo, grande desiderio. Non è sempre stato facile: abbiamo litigato, riso, pianto – e capito quanto apparteniamo l'uno all'altra.</p>
                <p>Nel luglio 2023 è nato nostro figlio – il nostro secondo bimbo desiderato che ha completato la nostra famiglia. Con i nostri due figli, da "io e te" siamo diventati un vero "noi".</p>
                <p>Una sera di novembre 2024, seduti sul divano sotto una coperta, guardavamo la TV. In un programma un uomo chiedeva alla sua compagna di sposarlo – un momento che ci fece sorridere entrambi.</p>
                <p>Lui si girò verso di me e disse tranquillamente: "In realtà non esiste mai il momento perfetto."<br/>Io lo guardai, sorrisi e risposi: "Ah sì? E allora cosa stai aspettando?" 😉</p>
                <p>Abbiamo riso, scherzato e alla fine era chiaro: il 12.12.2024 sarebbe stato il nostro giorno. Lui disse: "Se riesci a sistemare tutti i documenti al consolato entro allora, ci sposiamo in comune."<br/>E – ovviamente – ce l'ha fatta! 💪</p>
                <p>Non c'è mai stata una proposta classica – ma la nostra storia non è mai stata classica. È vera, un po' caotica, piena di amore e di vita.</p>
                <p className="text-xl font-semibold text-center mt-8">Ora inizia il prossimo grande capitolo:<br/>ci sposeremo in chiesa in Italia – proprio come lo abbiamo sempre sognato.</p>
                <p className="text-center italic">Abbiamo vissuto tanto, riso, a volte litigato, ma ci siamo sempre ritrovati – e abbiamo imparato una cosa:</p>
                <p className="text-2xl text-center font-semibold">✨ Solo insieme siamo davvero forti. ✨</p>
                <p className="text-center italic">Il nostro amore è cresciuto – dall'amicizia, dalla pazienza e dal destino.</p>
                <p className="text-2xl text-center font-bold mt-8">Per sempre inizia proprio qui. 💕</p>
              </>
            ) : language === 'pl' ? (
              <>
                <p className="text-xl italic text-center mb-8">
                  Czasami życie pisze historie tak prawdziwe, że są piękniejsze niż jakakolwiek baśń – nasza jest właśnie taka.
                </p>
                <p>Pierwszy raz nasze drogi skrzyżowały się, gdy mieliśmy 14 lat – w szkole. Już wtedy on zwrócił na mnie uwagę i po cichu mnie podziwiał, a ja byłam zainteresowana wszystkim, tylko nie chłopakami. 😄</p>
                <p>Później uczęszczaliśmy do tej samej szkoły zawodowej o profilu gospodarstwa domowego. Dobrze się dogadywaliśmy, ale między nami była tylko przyjaźń. Minęły lata, aż pewnej nocy przypadkiem spotkaliśmy się w klubie, w którym on pracował. To jednak nie był właściwy moment – miał dziewczynę, a ja dostałam swoją pierwszą małą „kosza”.</p>
                <p>Od czasu do czasu pisał do mnie na Facebooku krótkie wiadomości typu „Co słychać?”, ale z czasem ta historia znów ucichła.</p>
                <p>Dopiero pod koniec 2018 roku spotkaliśmy się ponownie – zupełnie przypadkiem, w barze. Przywitałam znajomego i na początku nawet go nie zauważyłam, co oczywiście trochę go uraziło 😉. Potem zaczęliśmy rozmawiać, śmiać się i postanowiliśmy, że kiedyś pójdziemy razem potańczyć. Dwa tygodnie później tak się stało – beztroska noc, bez oczekiwań, pełna zabawy i lekkości.</p>
                <p>Wkrótce znów spędzaliśmy razem czas, oglądając filmy – i na początku 2019 roku stało się jasne, że między nami jest coś więcej. Nasza więź stała się silniejsza, prawdziwsza, głębsza.</p>
                <p className="font-semibold">Ale wkrótce życie wystawiło nas na pierwszą poważną próbę.</p>
                <p>Niespodziewany cios losu zachwiał naszym młodym związkiem. To był czas pełen emocji, wątpliwości i bólu – ale też moment, w którym nauczyliśmy się, co znaczy naprawdę być dla siebie. Trzymaliśmy się razem, nawet kiedy było ciężko, i właśnie to jeszcze bardziej nas zbliżyło.</p>
                <p>W listopadzie 2020 na świat przyszła nasza córka – nasze wymarzone dziecko. Oczywiście nie zawsze było łatwo: kłóciliśmy się, śmialiśmy, płakaliśmy – i krok po kroku odkrywaliśmy, jak bardzo do siebie należymy.</p>
                <p>W lipcu 2023 urodził się nasz syn – nasze drugie wymarzone dziecko, które dopełniło naszą rodzinę. Z dwójką dzieci z „ty i ja” staliśmy się prawdziwym „my”.</p>
                <p>Pewnego spokojnego wieczoru w listopadzie 2024 siedzieliśmy razem na kanapie, wtuleni pod kocem i oglądaliśmy telewizję. W programie mężczyzna oświadczał się swojej partnerce – moment, który sprawił, że oboje się uśmiechnęliśmy.</p>
                <p>On spojrzał na mnie i spokojnie powiedział: „Właściwie nigdy nie ma idealnego momentu."<br/>Spojrzałam na niego, uśmiechnęłam się i odpowiedziałam: „Aha? To na co jeszcze czekasz?” 😉</p>
                <p>Śmialiśmy się, żartowaliśmy i w końcu stało się jasne: 12.12.2024 będzie naszym dniem. Powiedział: „Jeśli uda ci się do tego czasu załatwić wszystkie papiery w konsulacie, weźmiemy ślub cywilny."<br/>I – oczywiście – udało mi się! 💪</p>
                <p>Nie było klasycznych oświadczyn – ale nasza historia nigdy nie była klasyczna. Jest prawdziwa, trochę chaotyczna, pełna miłości i życia.</p>
                <p className="text-xl font-semibold text-center mt-8">Teraz zaczyna się kolejny wielki rozdział:<br/>weźmiemy ślub kościelny we Włoszech – dokładnie tak, jak o tym marzyliśmy.</p>
                <p className="text-center italic">Wiele razem przeżyliśmy, dużo się śmialiśmy, czasem kłóciliśmy, zawsze znów do siebie wracaliśmy – i nauczyliśmy się jednego:</p>
                <p className="text-2xl text-center font-semibold">✨ Tylko razem jesteśmy naprawdę silni. ✨</p>
                <p className="text-center italic">Nasza miłość dojrzewała – z przyjaźni, cierpliwości i przeznaczenia.</p>
                <p className="text-2xl text-center font-bold mt-8">Na zawsze zaczyna się właśnie tutaj. 💕</p>
              </>
            ) : (
              <>
                <p className="text-xl italic text-center mb-8">
                  Manchmal schreibt das Leben Geschichten, die so echt sind, dass sie schöner sind als jedes Märchen – und genau so ist es bei uns.
                </p>
                <p>Unsere Wege kreuzten sich zum ersten Mal, als wir 14 Jahre alt waren – in der Schule. Schon damals fiel ich ihm auf, und er hat mich heimlich ein bisschen bewundert. Ich dagegen war zu dieser Zeit eher an allem interessiert – nur nicht an Jungs. 😄</p>
                <p>Später besuchten wir beide die hauswirtschaftliche Schule. Wir verstanden uns gut, aber mehr als Freundschaft war damals nicht zwischen uns. Jahre vergingen, bis wir uns eines Nachts zufällig in einer Disco wiederbegegneten, in der er arbeitete. Doch das Timing war nicht richtig – er war vergeben, und ich bekam meine erste kleine Abfuhr.</p>
                <p>Ab und zu schrieb er mir über Facebook, kleine Nachrichten wie „Wie geht's?" – doch die Geschichte schlief wieder ein.</p>
                <p>Erst Ende 2018 kreuzten sich unsere Wege erneut – ganz zufällig, in einer Bar. Ich begrüßte einen Bekannten und bemerkte ihn zunächst gar nicht, was ihn natürlich ein wenig beleidigte 😉. Doch dann kamen wir ins Gespräch, lachten und beschlossen, irgendwann gemeinsam feiern zu gehen. Zwei Wochen später war es soweit – eine unbeschwerte Nacht, ganz ohne Erwartungen, einfach voller Spaß und Leichtigkeit.</p>
                <p>Kurz darauf verbrachten wir wieder Zeit miteinander, schauten Filme – und Anfang 2019 war es schließlich um uns geschehen. Unsere Verbindung wurde stärker, echter, tiefer.</p>
                <p className="font-semibold">Doch schon bald stellte uns das Leben auf die erste große Probe.</p>
                <p>Ein Schicksalsschlag traf uns völlig unerwartet und brachte unsere junge Beziehung ins Wanken. Es war eine Zeit voller Emotionen, Zweifel und Schmerz – aber auch eine Zeit, in der wir lernten, was es bedeutet, füreinander da zu sein. Wir hielten zusammen, auch wenn es schwer war, und genau das hat uns noch enger verbunden.</p>
                <p>Im November 2020 wurden wir mit unserer Tochter gesegnet – unserem absoluten Wunschkind. Natürlich war nicht immer alles einfach: Wir haben gestritten, gelacht, geweint – und dabei gelernt, wie sehr wir zusammengehören.</p>
                <p>Im Juli 2023 kam unser Sohn zur Welt – unser zweites Wunschkind, das unsere Familie vollkommen gemacht hat. Mit unseren beiden Kindern wurde aus „du und ich" ein echtes Wir.</p>
                <p>Dann, eines gemütlichen Abends im November 2024, saßen wir gemeinsam auf dem Sofa, kuschelten uns unter eine Decke und sahen fern. In der Sendung machte ein Mann seiner Partnerin einen Antrag – ein Moment, der uns beide zum Lächeln brachte.</p>
                <p>Er drehte sich zu mir und sagte ruhig: „Naja, es gibt nie den perfekten Zeitpunkt dafür."<br/>Ich sah ihn an, grinste und antwortete: „Aha, und worauf wartest du?" 😉</p>
                <p>Wir lachten, redeten herum und irgendwann stand fest: Der 12.12.2024 sollte unser Tag werden. Er meinte: „Wenn du es schaffst, bis dahin alle Papiere beim Konsulat zu regeln, dann heiraten wir standesamtlich."<br/>Und – natürlich – ich habe es geschafft! 💪</p>
                <p>Einen klassischen Antrag gab es nicht – aber unsere Geschichte war noch nie klassisch. Sie war echt, chaotisch, liebevoll und voller Leben.</p>
                <p className="text-xl font-semibold text-center mt-8">Jetzt steht das nächste große Kapitel an:<br/>Wir werden kirchlich in Italien heiraten – so, wie wir es uns immer erträumt haben.</p>
                <p className="text-center italic">Wir haben viel erlebt, viel gelacht, manchmal gestritten, immer wieder zusammengefunden – und eines gelernt:</p>
                <p className="text-2xl text-center font-semibold">✨ Nur gemeinsam sind wir stark. ✨</p>
                <p className="text-center italic">Unsere Liebe ist gewachsen – aus Freundschaft, aus Geduld, aus Schicksal.</p>
                <p className="text-2xl text-center font-bold mt-8">Für immer beginnt genau hier. 💕</p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Details / Timeline */}
      <section
        ref={detailsRef}
        id="details"
        className={`py-20 bg-[#e9dfcf] relative overflow-hidden section-animate ${isVisible.details ? 'visible' : ''}`}
      >
        <div 
          className="absolute opacity-20 parallax-bg" 
          style={{
            backgroundImage: 'url(/IMG-20251117-WA0001.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            top: '-30%',
            left: 0,
            right: 0,
            bottom: '-30%',
            transform: `translateY(${scrollY * 0.1}px)`
          }}
        />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-10">
            <div className="text-4xl md:text-5xl mb-2 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              J & D
            </div>
            <div className="h-px w-24 bg-black mx-auto mb-4"></div>
            <p className="text-xl md:text-2xl mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Unsere Hochzeitsplanung
            </p>
            <p className="text-sm md:text-base tracking-[0.3em] uppercase text-gray-500">
              Am 11.08.26
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-center mb-12 text-gray-700 leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.timelineIntro}
            </p>

            <div
              className="max-w-3xl mx-auto mb-10 rounded-2xl border border-amber-500/50 bg-amber-500/10 shadow-lg px-6 py-4 md:px-8 md:py-5"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              <div className="flex items-start justify-center gap-3">
                <span className="text-2xl md:text-3xl leading-none">⏰</span>
                <p className="text-base md:text-lg text-center text-amber-950 font-semibold leading-relaxed">
                  {t.timelinePunctualNotice}
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="mt-10">
              <div className="relative px-3">
                {/* Vertical center line */}
                <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 bg-black h-full"></div>

                {[
                  {
                    time: '10:30',
                    title: t.timelineCeremonyTitle,
                    desc: t.timelineCeremonyDesc,
                    icon: '/ringe.svg'
                  },
                  {
                    time: '13:00',
                    title: t.timelineArrivalTitle,
                    desc: t.timelineArrivalDesc,
                    icon: '/weinglass.svg'
                  },
                  {
                    time: '13:30',
                    title:
                      language === 'de'
                        ? 'Fotos'
                        : language === 'en'
                          ? 'Photos'
                          : language === 'it'
                            ? 'Foto'
                            : 'Zdjęcia',
                    desc:
                      language === 'de'
                        ? 'Fotomomente mit dem Brautpaar.'
                        : language === 'en'
                          ? 'Photo moments with the couple.'
                          : language === 'it'
                            ? 'Momenti foto con gli sposi.'
                            : 'Chwile zdjęciowe z parą młodą.',
                    icon: '/foto.svg'
                  },
                  {
                    time: '14:30',
                    title: t.timelineLunchTitle,
                    desc: t.timelineLunchDesc,
                    icon: '/teller.svg'
                  },
                  {
                    time: '19:30',
                    title: t.timelineDessertTitle,
                    desc: t.timelineDessertDesc,
                    icon: '/cupcake.svg'
                  },
                  {
                    time: '21:00',
                    title: t.timelineCakeTitle,
                    desc: t.timelineCakeDesc,
                    icon: '/torte.svg'
                  },
                  {
                    time: '22:00',
                    title: t.timelinePartyTitle,
                    desc: t.timelinePartyDesc,
                    icon: '/disco.svg'
                  }
                ].map((item, index) => (
                  <div key={index} className="relative flex items-start mb-10 last:mb-0">
                    {/* Dot in center */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-3 h-3 rounded-full bg-amber-600 border-2 border-white shadow-md z-10 mt-3"></div>

                    {/* Content - alternating sides, ohne Karten */}
                    {index % 2 === 0 ? (
                      <>
                        <div className="w-[45%] pr-4 text-right">
                          <div className="flex justify-end mb-2">
                            <img
                              src={item.icon}
                              alt={`${item.title} icon`}
                              className={item.icon === '/teller.svg' ? "w-24 h-24 md:w-32 md:h-32 object-contain" : "w-20 h-20 md:w-24 md:h-24 object-contain"}
                            />
                          </div>
                          <p className="text-xl md:text-2xl font-semibold text-gray-800 tracking-wide">
                            {item.time} Uhr
                          </p>
                          <p
                            className="text-lg md:text-2xl font-semibold text-gray-900 mt-1"
                            style={{ fontFamily: "'Cormorant Garamond', serif" }}
                          >
                            {item.title}
                          </p>
                          <p className="text-sm md:text-base text-gray-700 mt-2 leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                        <div className="w-[10%]"></div>
                        <div className="w-[45%]"></div>
                      </>
                    ) : (
                      <>
                        <div className="w-[45%]"></div>
                        <div className="w-[10%]"></div>
                        <div className="w-[45%] pl-4 text-left">
                          <div className="flex justify-start mb-2">
                            <img
                              src={item.icon}
                              alt={`${item.title} icon`}
                              className={item.icon === '/teller.svg' ? "w-24 h-24 md:w-32 md:h-32 object-contain" : "w-20 h-20 md:w-24 md:h-24 object-contain"}
                            />
                          </div>
                          <p className="text-xl md:text-2xl font-semibold text-gray-800 tracking-wide">
                            {item.time} Uhr
                          </p>
                          <p
                            className="text-lg md:text-2xl font-semibold text-gray-900 mt-1"
                            style={{ fontFamily: "'Cormorant Garamond', serif" }}
                          >
                            {item.title}
                          </p>
                          <p className="text-sm md:text-base text-gray-700 mt-2 leading-relaxed">
                            {item.desc}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-16 text-center bg-white/70 rounded-xl px-6 py-8">
            <h3 className="text-3xl mb-4 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.dressCode}
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              {t.dressCodeText}
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              {[
                '#B0E0E6', // Helles Blau
                '#D3D9CA', // Salbeigrün hell
                '#C8DDB5', // Hellgrün
                '#F0E68C', // Helles Gelb
                '#E8E4D0'  // Warmes Beige
              ].map((color, i) => (
                <div key={i} className="w-16 h-16 rounded-full border-4 border-white shadow-lg" style={{ backgroundColor: color }}></div>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-600 italic">
              {t.dressCodeColorPalette}
            </p>
          </div>
        </div>
      </section>

      {/* RSVP & Guestbook */}
      <section
        ref={rsvpRef}
        id="rsvp"
        className={`py-20 relative overflow-hidden section-animate ${isVisible.rsvp ? 'visible' : ''}`}
        style={{ backgroundColor: '#2d2d2d' }}
      >
        <div 
          className="absolute inset-0 rsvp-bg" 
          style={{
            backgroundImage: 'url(/rsvpgäste.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat'
          }}
        ></div>
        <div className="absolute inset-0 bg-black/40"></div>

        {/* RSVP Block */}
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="text-4xl md:text-5xl text-center mb-4 tracking-wider text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t.rsvp}
          </h2>
          <div className="h-px w-24 bg-white mx-auto mb-4"></div>
          <p className="text-center text-white/90 mb-8">
            {language === 'de' && 'Bei dringenden Fragen: '}
            {language === 'en' && 'For urgent questions: '}
            {language === 'it' && 'Per domande urgenti: '}
            {language === 'pl' && 'W pilnych sprawach: '}
            <a href="tel:+4901795270458" className="text-white font-semibold hover:underline">01795270458</a>
          </p>
          <p className="text-center text-white mb-12 uppercase tracking-widest text-sm">
            {t.respondBy}
          </p>

          <div className="space-y-6 bg-white/95 backdrop-blur-sm p-8 rounded-lg">
              {/* Step 1: Name - Always visible */}
              <div className="grid md:grid-cols-2 gap-6 animate-fadeIn">
                <div>
                  <label className="block text-sm uppercase tracking-widest mb-2">{t.firstName} *</label>
                  <input
                    type="text"
                    required
                    value={rsvpForm.firstName}
                    onChange={(e) => setRsvpForm({ ...rsvpForm, firstName: e.target.value })}
                    className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm uppercase tracking-widest mb-2">{t.lastName} *</label>
                  <input
                    type="text"
                    required
                    value={rsvpForm.lastName}
                    onChange={(e) => setRsvpForm({ ...rsvpForm, lastName: e.target.value })}
                    className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-all"
                  />
                </div>
              </div>

              {/* Step 2: Email - Visible when name is filled */}
              <div className={`transition-all duration-500 overflow-hidden ${
                rsvpForm.firstName && rsvpForm.lastName 
                  ? 'max-h-40 opacity-100' 
                  : 'max-h-0 opacity-0'
              }`}>
                <label className="block text-sm uppercase tracking-widest mb-2">{t.email} *</label>
                <input
                  type="email"
                  required
                  value={rsvpForm.email}
                  onChange={(e) => setRsvpForm({ ...rsvpForm, email: e.target.value })}
                  className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-all"
                />
              </div>

              {/* Step 3: Attending - Visible when email is filled */}
              <div className={`transition-all duration-500 overflow-hidden ${
                rsvpForm.firstName && rsvpForm.lastName && rsvpForm.email 
                  ? 'max-h-60 opacity-100' 
                  : 'max-h-0 opacity-0'
              }`}>
                <label className="block text-lg mb-6 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {t.willYouAttend} *
                </label>
                <div className="flex gap-8 justify-center">
                  <label className="flex flex-col items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="attending"
                      checked={rsvpForm.attending === true}
                      onChange={() => setRsvpForm({ ...rsvpForm, attending: true })}
                      className="hidden"
                    />
                    <div className={`w-20 h-20 border-4 rounded-full flex items-center justify-center transition-all ${
                      rsvpForm.attending === true ? 'border-black bg-black' : 'border-gray-300 bg-white'
                    }`}>
                      <Heart className={`w-10 h-10 ${rsvpForm.attending === true ? 'text-white fill-white' : 'text-gray-300'}`} />
                    </div>
                    <span className="text-sm uppercase tracking-widest text-gray-900">{t.yesAttending}</span>
                  </label>
                  <label className="flex flex-col items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="attending"
                      checked={rsvpForm.attending === false}
                      onChange={() => setRsvpForm({ ...rsvpForm, attending: false })}
                      className="hidden"
                    />
                    <div className={`w-20 h-20 border-4 rounded-full flex items-center justify-center transition-all ${
                      rsvpForm.attending === false ? 'border-black bg-black' : 'border-gray-300 bg-white'
                    }`}>
                      <X className={`w-10 h-10 ${rsvpForm.attending === false ? 'text-white' : 'text-gray-300'}`} strokeWidth={3} />
                    </div>
                    <span className="text-sm uppercase tracking-widest text-gray-900">{t.noAttending}</span>
                  </label>
                </div>
              </div>

              {/* Step 4: Details if attending - Visible when attending=true */}
              <div className={`transition-all duration-500 overflow-hidden ${
                rsvpForm.attending === true 
                  ? 'max-h-[2000px] opacity-100' 
                  : 'max-h-0 opacity-0'
              }`}>
                <div className="space-y-6">
                  <div className="border-2 border-gray-300 p-6 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm uppercase tracking-widest">{t.guests}</label>
                      <button
                        type="button"
                        onClick={addGuest}
                        className="text-sm bg-black text-white px-4 py-2 uppercase tracking-widest hover:bg-gray-800"
                      >
                        {t.addGuest}
                      </button>
                    </div>
                    <p className="text-xs text-red-600 mb-4 font-semibold">{t.ageNote}</p>
                    
                    {rsvpForm.guests.map((guest, index) => (
                      <div key={index} className="grid grid-cols-12 gap-3 mb-3 items-end">
                        <div className="col-span-8">
                          <label className="block text-xs mb-1 text-gray-600">{t.guestName}</label>
                          <input
                            type="text"
                            value={guest.name}
                            onChange={(e) => updateGuest(index, 'name', e.target.value)}
                            className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none bg-white"
                          />
                        </div>
                        <div className="col-span-3">
                          <label className="block text-xs mb-1 text-gray-600">{t.guestAge}</label>
                          <input
                            type="number"
                            value={guest.age}
                            onChange={(e) => updateGuest(index, 'age', e.target.value)}
                            className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none bg-white"
                            min="0"
                          />
                        </div>
                        {rsvpForm.guests.length > 1 && (
                          <div className="col-span-1">
                            <button
                              type="button"
                              onClick={() => removeGuest(index)}
                              className="w-full bg-black text-white p-3 hover:bg-gray-800 text-lg"
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm uppercase tracking-widest mb-2">
                      {t.dietary}
                    </label>
                    <select
                      value={rsvpForm.dietaryRestrictions}
                      onChange={(e) => setRsvpForm({ ...rsvpForm, dietaryRestrictions: e.target.value })}
                      className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none"
                    >
                      <option value="">{t.pleaseSelect}</option>
                      <option value="vegetarian">{t.vegetarian}</option>
                      <option value="vegan">{t.vegan}</option>
                      <option value="fish">{t.fish}</option>
                      <option value="chicken">{t.chicken}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm uppercase tracking-widest mb-2">
                      {t.allergies}
                    </label>
                    <textarea
                      value={rsvpForm.allergies}
                      onChange={(e) => setRsvpForm({ ...rsvpForm, allergies: e.target.value })}
                      className="w-full border-2 border-gray-300 p-3 h-24 focus:border-black focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Step 5: Message + Submit - Visible when attending is selected */}
              <div className={`transition-all duration-500 overflow-hidden ${
                rsvpForm.attending !== null 
                  ? 'max-h-80 opacity-100' 
                  : 'max-h-0 opacity-0'
              }`}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm uppercase tracking-widest mb-2">
                      {t.message}
                    </label>
                    <textarea
                      value={rsvpForm.message}
                      onChange={(e) => setRsvpForm({ ...rsvpForm, message: e.target.value })}
                      className="w-full border-2 border-gray-300 p-3 h-24 focus:border-black focus:outline-none"
                      placeholder={t.messagePlaceholder}
                    />
                  </div>

                  <button
                    onClick={handleRSVPSubmit}
                    className="w-full bg-black text-white py-4 px-6 uppercase tracking-widest text-sm hover:bg-gray-800 transition-all duration-300 transform hover:scale-[1.02]"
                  >
                    {t.submitRSVP}
                  </button>
                </div>
              </div>
            </div>
        </div>

        {/* Guestbook Block */}
        <div
          ref={guestbookRef}
          id="guestbook"
          className="max-w-4xl mx-auto px-6 relative z-10 pt-20"
        >
          <div className="text-center mb-12 animate-fadeIn">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-white" />
            <h2 className="text-4xl md:text-5xl mb-4 tracking-wider text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.guestbook}
            </h2>
            <div className="h-px w-24 bg-white mx-auto mb-4"></div>
            <p className="text-lg text-white/80 italic">{t.guestbookSubtitle}</p>
          </div>

          {guestbookSubmitted ? (
            <div className="text-center py-16 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg animate-fadeIn mb-12">
              <div className="text-6xl mb-4">❤️</div>
              <h3 className="text-3xl mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {t.guestbookThankYou}
              </h3>
            </div>
          ) : (
            <div className="bg-white/80 backdrop-blur-sm p-8 rounded-lg shadow-lg mb-12 transform hover:shadow-2xl transition-all duration-300">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm uppercase tracking-widest mb-2">{t.guestbookName} *</label>
                  <input
                    type="text"
                    required
                    value={guestbookForm.name}
                    onChange={(e) => setGuestbookForm({ ...guestbookForm, name: e.target.value })}
                    className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm uppercase tracking-widest mb-2">{t.guestbookMessage} *</label>
                  <textarea
                    required
                    value={guestbookForm.message}
                    onChange={(e) => setGuestbookForm({ ...guestbookForm, message: e.target.value })}
                    className="w-full border-2 border-gray-300 p-3 h-32 focus:border-black focus:outline-none transition-all"
                  />
                </div>
                <button
                  onClick={handleGuestbookSubmit}
                  className="w-full bg-black text-white py-4 uppercase tracking-widest hover:bg-gray-800 transition-all text-sm flex items-center justify-center gap-2 group"
                >
                  <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  {t.guestbookSubmit}
                </button>
              </div>
            </div>
          )}

          {/* Display Guestbook Messages as Vertical Carousel */}
          <div className="min-h-[180px] relative">
            {guestbookMessages.length > 0 ? (
              <div
                key={guestbookMessages[guestbookIndex]?.id || 'carousel-item'}
                className="bg-white/70 backdrop-blur-sm p-8 rounded-xl shadow-lg animate-fadeIn"
              >
                <div className="flex items-start gap-4">
                  <Heart className="w-8 h-8 text-red-400 flex-shrink-0 mt-1" fill="currentColor" />
                  <div className="flex-1">
                    <p className="text-gray-700 mb-3 italic text-xl leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      "{guestbookMessages[guestbookIndex]?.message}"
                    </p>
                    <p className="text-sm text-gray-500">
                      — {guestbookMessages[guestbookIndex]?.name}
                      <span className="ml-2 text-xs">
                        {guestbookMessages[guestbookIndex]?.createdAt 
                          ? new Date(guestbookMessages[guestbookIndex].createdAt).toLocaleDateString(language === 'de' ? 'de-DE' : language === 'it' ? 'it-IT' : language === 'pl' ? 'pl-PL' : 'en-US')
                          : ''}
                      </span>
                    </p>
                  </div>
                </div>
                {guestbookMessages.length > 1 && (
                  <div className="mt-6 flex justify-center gap-2">
                    {guestbookMessages.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setGuestbookIndex(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          i === guestbookIndex ? 'bg-gray-800 scale-125' : 'bg-gray-400/50 hover:bg-gray-500'
                        }`}
                        aria-label={`Eintrag ${i + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 bg-white/50 rounded-lg">
                <Heart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="italic">Noch keine Gästebuch-Einträge vorhanden.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section
        ref={photosSectionRef}
        id="photos"
        className={`py-20 bg-neutral-100 relative overflow-hidden section-animate ${isVisible.photos ? 'visible' : ''}`}
      >
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12 animate-fadeIn">
            <Camera className="w-12 h-12 mx-auto mb-4 text-gray-700" />
            <h2 className="text-4xl md:text-5xl mb-4 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.photos}
            </h2>
            <div className="h-px w-24 bg-black mx-auto mb-4"></div>
            <p className="text-lg text-gray-600 italic">{t.photosSubtitle}</p>
          </div>

          {/* Upload */}
          <div className="bg-white p-8 rounded-lg shadow-lg mb-12 max-w-2xl mx-auto transform hover:shadow-2xl transition-all duration-300">
            {photoUploadDone ? (
              <div className="text-center py-8 animate-fadeIn">
                <div className="text-5xl mb-4">📸</div>
                <h3 className="text-2xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {t.photosThankYou}
                </h3>
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm uppercase tracking-widest mb-2">{t.photosName} *</label>
                  <input
                    type="text"
                    required
                    value={photoName}
                    onChange={(e) => setPhotoName(e.target.value)}
                    className="w-full border-2 border-gray-300 p-3 focus:border-black focus:outline-none transition-all"
                  />
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
                <button
                  onClick={() => photoInputRef.current && photoInputRef.current.click()}
                  disabled={photoUploading}
                  className="w-full bg-black text-white py-4 uppercase tracking-widest hover:bg-gray-800 transition-all text-sm flex items-center justify-center gap-2 group disabled:opacity-60 disabled:cursor-wait"
                >
                  <Camera className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  {photoUploading ? `${t.photosUploading} ${photoUploadProgress}` : t.photosSelect}
                </button>
                <p className="text-xs text-gray-500 text-center">{t.photosHint}</p>
              </div>
            )}
          </div>

          {/* Grid */}
          {photos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {photos.map((photo, index) => (
                <button
                  key={photo.id}
                  onClick={() => openLightbox(index)}
                  className="relative group aspect-square overflow-hidden rounded-lg shadow-md bg-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                >
                  <img
                    src={photo.thumb}
                    alt={`Foto von ${photo.name}`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs truncate">{photo.name}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 bg-white/70 rounded-lg">
              <Camera className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="italic">{t.photosEmpty}</p>
            </div>
          )}
        </div>

        {/* Lightbox */}
        {lightboxIndex !== null && photos[lightboxIndex] && (
          <div
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setLightboxIndex(null)}
          >
            <button
              onClick={() => setLightboxIndex(null)}
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10"
              aria-label="Schließen"
            >
              <X className="w-8 h-8" />
            </button>
            {photos.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openLightbox((lightboxIndex - 1 + photos.length) % photos.length);
                }}
                className="absolute left-2 md:left-6 text-white/70 hover:text-white p-2 z-10"
                aria-label="Vorheriges Foto"
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
            )}
            <div className="max-w-4xl max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <img
                src={fullImages[photos[lightboxIndex].id] || photos[lightboxIndex].thumb}
                alt={`Foto von ${photos[lightboxIndex].name}`}
                className="max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl"
              />
              <p className="text-white/80 text-sm mt-4">
                {photos[lightboxIndex].name}
                <span className="ml-2 text-white/50 text-xs">
                  {photos[lightboxIndex].createdAt
                    ? new Date(photos[lightboxIndex].createdAt).toLocaleDateString(language === 'de' ? 'de-DE' : language === 'it' ? 'it-IT' : language === 'pl' ? 'pl-PL' : 'en-US')
                    : ''}
                </span>
              </p>
            </div>
            {photos.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openLightbox((lightboxIndex + 1) % photos.length);
                }}
                className="absolute right-2 md:right-6 text-white/70 hover:text-white p-2 z-10"
                aria-label="Nächstes Foto"
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            )}
          </div>
        )}
      </section>

      {/* Hotel Recommendations Section */}
      <section
        ref={hotelsRef}
        id="hotels"
        className={`py-20 bg-white relative overflow-hidden section-animate ${isVisible.hotels ? 'visible' : ''}`}
      >
        <div 
          className="absolute inset-0 opacity-5" 
          style={{
            backgroundImage: 'url(/IMG-20251117-WA0002.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.15}px)`
          }}
        />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12 animate-fadeIn">
            <Hotel className="w-12 h-12 mx-auto mb-4 text-gray-700" />
            <h2 className="text-4xl md:text-5xl mb-4 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.hotels}
            </h2>
            <div className="h-px w-24 bg-black mx-auto mb-4"></div>
            <p className="text-lg text-gray-600 italic">{t.hotelsSubtitle}</p>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="bg-white/90 backdrop-blur-sm p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover-card">
              <div className="mb-4">
                <h3 className="text-2xl mb-2 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  ibis styles Trani
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <MapPin className="w-4 h-4" />
                  <span>Corso M. R. Imbriani 137, 76125 Trani, Italien</span>
                </div>
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed">
                {language === 'de' ? 'Unser empfohlenes Hotel für euren Aufenthalt in Trani. Perfekt gelegen für unsere Hochzeitsfeier am 11. August 2026.' : 
                 language === 'it' ? 'Il nostro hotel consigliato per il vostro soggiorno a Trani. Posizione perfetta per la nostra festa di nozze l\'11 agosto 2026.' :
                 language === 'pl' ? 'Nasze polecane hotel dla Państwa pobytu w Trani. Idealna lokalizacja na naszą uroczystość ślubną 11 sierpnia 2026.' :
                 'Our recommended hotel for your stay in Trani. Perfectly located for our wedding celebration on August 11, 2026.'}
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <Phone className="w-4 h-4" />
                <span>+39 0883 588010</span>
              </div>
              <a
                href="https://www.booking.com/hotel/it/trani.de.html?aid=356980&label=gog235jc-10CAsocUIFdHJhbmlIB1gDaDuIAQGYATO4AQfIAQ3YAQPoAQH4AQGIAgGoAgG4ApuskckGwAIB0gIkZGE2ZWJmZTEtNWE5YS00ZmZmLWE1ZjktZjQzZjVkMWZjY2Ji2AIB4AIB&sid=52657f0035c5733cdf94d6ef3701a96d&all_sr_blocks=114578507_93628509_2_1_0&checkin=2026-08-10&checkout=2026-08-12&dest_id=-131185&dest_type=city&dist=0&group_adults=2&group_children=0&hapos=1&highlighted_blocks=114578507_93628509_2_1_0&hpos=1&matching_block_id=114578507_93628509_2_1_0&no_rooms=1&req_adults=2&req_children=0&room1=A%2CA&sb_price_type=total&sr_order=popularity&sr_pri_blocks=114578507_93628509_2_1_0__82800&srepoch=1764025786&srpvid=e9f3471eaa1f6259c02a2507fdbbc72f&type=total&ucfs=1#availability"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded hover:bg-gray-800 transition-all group"
              >
                <Globe className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                Jetzt auf Booking.com buchen
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section
        ref={locationRef}
        id="location"
        className={`py-20 bg-[#e5e1d8] transition-all duration-1000 section-animate ${isVisible.location ? 'visible' : ''}`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl md:text-5xl text-center mb-4 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t.location}
          </h2>
          <div className="h-px w-24 bg-black mx-auto mb-12"></div>

          {/* Trauung – Kathedrale */}
          <div className="mb-12">
            <h3 className="text-xl md:text-2xl mb-4 tracking-wider text-black" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {language === 'de' && 'Kirchliche Trauung'}
              {language === 'en' && 'Church ceremony'}
              {language === 'it' && 'Cerimonia religiosa'}
              {language === 'pl' && 'Ceremonia kościelna'}
            </h3>
            <p className="text-gray-700 mb-4">Kathedrale von Trani · 10:30 Uhr</p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 shadow-lg">
                <h4 className="text-lg mb-3 tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t.gettingThere}</h4>
                <p className="text-sm text-gray-700">{t.parkingInfo}</p>
                <p className="text-sm text-gray-700 mt-2">{t.tip} {t.tipText}</p>
              </div>
              <div className="aspect-square bg-gray-200 overflow-hidden shadow-lg">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2998.225315046363!2d16.415785576539!3d41.282200502512445!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1338040b58cc7ce3%3A0x880f0c96b60233ee!2sKathedrale%20von%20Trani!5e0!3m2!1sde!2sde!4v1763453352411!5m2!1sde!2sde" 
                  width="100%" 
                  height="100%" 
                  style={{border: 0}} 
                  allowFullScreen={true}
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Kathedrale von Trani"
                />
              </div>
            </div>
          </div>

          {/* Feier – Tenuta Donna Lavinia */}
          <div>
            <h3 className="text-xl md:text-2xl mb-4 tracking-wider text-black" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {language === 'de' && 'Feier nach der Trauung'}
              {language === 'en' && 'Reception'}
              {language === 'it' && 'Ricevimento'}
              {language === 'pl' && 'Przyjęcie'}
            </h3>
            <div className="bg-white p-8 shadow-lg">
              <p className="font-semibold text-lg text-gray-900">Tenuta Donna Lavinia</p>
              <p className="text-gray-700 mt-2">Via Capirro Primo 3</p>
              <p className="text-gray-700">76125 Trani BT</p>
              <p className="text-gray-700">Italia</p>
              <a 
                href="https://www.google.com/maps/search/?api=1&query=Tenuta+Donna+Lavinia+Via+Capirro+Primo+3+76125+Trani+BT+Italia" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-black font-medium hover:underline"
              >
                <MapPin className="w-4 h-4" />
                {language === 'de' && 'In Google Maps öffnen'}
                {language === 'en' && 'Open in Google Maps'}
                {language === 'it' && 'Apri in Google Maps'}
                {language === 'pl' && 'Otwórz w Google Maps'}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="pt-16 pb-12 text-center bg-gradient-to-b from-neutral-900 via-neutral-950 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, #d4af37 0%, transparent 50%)' }}></div>
        <div className="relative z-10">
          <div className="flex flex-col items-center gap-6 mb-8">
            <img
              src="/logo2.png"
              alt="Joanna & Domenico Logo"
              className="h-16 w-16 object-contain opacity-90 hover:scale-110 transition-transform duration-300"
            />
            <div 
              className="text-5xl md:text-6xl gold-text px-8 py-4 overflow-visible" 
              style={{ 
                fontFamily: "'Great Vibes', cursive", 
                lineHeight: '1.8',
                textShadow: '0 2px 20px rgba(212, 175, 55, 0.3)'
              }}
            >
              Joanna & Domenico
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500/50"></div>
            <Heart className="w-5 h-5 text-amber-400" fill="currentColor" />
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500/50"></div>
          </div>
          <p className="text-xl uppercase tracking-[0.4em] text-white/90 font-light">11 • 08 • 2026</p>
          <p className="text-base mt-4 text-amber-200/70">Trani, Puglia · Italia 🇮🇹</p>
          <p className="text-sm mt-3 text-white/40 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>"L'amore è eterno"</p>
        </div>
        <div className="mt-8 pt-6 border-t border-white/10">
          <p className="text-[10px] text-gray-500">
            Website erstellt mit ♥ von SyncIT
          </p>
        </div>
      </footer>

      {/* RSVP Thank You Popup */}
      {showRsvpPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-gradient-to-br from-white to-amber-50 rounded-2xl p-10 mx-4 max-w-md w-full text-center shadow-2xl border border-amber-200/50">
            <div className="text-6xl mb-4 float-subtle">💍</div>
            <h3 className="text-3xl mb-4 text-amber-800" style={{ fontFamily: "'Great Vibes', cursive" }}>
              {t.rsvpThankYou}
            </h3>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              {t.rsvpThankYouMessage}
            </p>
            <div className="flex justify-center">
              <Heart className="w-8 h-8 text-red-500 animate-pulse" fill="currentColor" />
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
}

export default WeddingOnePager;
